import { TeamConversion } from "../cases/TeamConversion";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

import { describeActionError } from "../../core-api/errors";
import type {
  CustomerBusinessProfile,
  DistributionCandidate,
  DistributionDecision,
  InquiryDetailProfile,
  InquiryHistorySummary,
  ProductRiskAssessment,
  ProductRiskProfile,
  ProductCommercialHistorySummary,
  ProductCommercialHistoryItem,
  TriageBusinessWorkbenchDetail,
  RealShadowInquiry,
  RealShadowDistributionResult,
  RealShadowGroupDistributionResult,
  FormalInquiryDistributionResult,
  HumanGmailSourcingStrategy,
  InternalRoleNotificationResult,
  RealShadowMergeResult,
  TriageDecisionProfile,
  TriageBusinessSemantics,
  MailJudgment,
  OriginalMailMessage,
  CrossWorkspaceDuplicateResponse,
} from "./api";
import {
  distributionResultLabel,
  downloadOriginalMailAttachment,
  loadCrossWorkspaceDuplicates,
  confirmRoleNotificationReceived,
  processingResultOf,
  retryRoleNotification,
} from "./api";
import { WorkbenchSecondaryPanelShell } from "./TriageWorkbenchShell";
import { MboxHistorySection } from "./MboxHistorySection";
import { MailPlainText, OriginalMailBody } from "./OriginalMailBody";
import { InlineHint } from "./InlineHint";
import type { FieldHint, HintPlacement } from "./InlineHint";
import { CrossWorkspaceDuplicateNotice } from "./CrossWorkspaceDuplicateNotice";
import {
  archiveDomainNote,
  archiveGradeSuggestion,
  CustomerHistoryBlock,
} from "./CustomerHistoryBlock";
import { useMboxHistory } from "./useMboxHistory";
import type { MboxHistoryLoadState } from "./useMboxHistory";
import { formatCountryDisplay } from "./country-display";

type Peek = "MAIL" | "CUSTOMER" | "HISTORY" | "RISK" | "DUPLICATE" | null;
export type ConfirmedInquiryFields = {
  productName: string;
  cas: string;
  catalogNumber: string;
  quantity: string;
  specification: string;
  intendedUse: string;
  destinationCountry: string;
};

export type CustomerProfileDraft = Pick<
  CustomerBusinessProfile,
  | "organizationName"
  | "country"
  | "customerGrade"
  | "profileNarrative"
  | "website"
> & {
  institutionIntroduction: string;
  industryResearchCapability: string;
  handlingRecommendation: string;
  domainVerificationDecision?: "CONFIRMED" | "REJECTED";
};
type ConfirmedMessageNature = "NEW_INQUIRY" | "PURCHASE_ORDER";

const invalidInquiryReasons = {
  SPAM_OR_MALICIOUS: "垃圾或恶意询单",
  FALSE_IDENTITY_OR_DEMAND: "明显虚假身份或需求",
  NO_VALID_PRODUCT_DEMAND: "无有效产品需求",
  REPEATED_HARASSMENT: "重复骚扰",
  OTHER: "其他无效原因",
} as const;
type InvalidInquiryReason = keyof typeof invalidInquiryReasons;

export const handledElsewhereReasons = {
  HANDLED_IN_GMAIL: "已在 Gmail 中处理",
  HANDLED_IN_OTHER_CHANNEL: "已在其他渠道处理",
  HISTORICAL_MIGRATION: "历史数据迁移，已在原流程处理",
  OTHER: "其他（已在别处处理）",
} as const;
export type HandledElsewhereReason = keyof typeof handledElsewhereReasons;

const customerTypeLabel: Record<
  CustomerBusinessProfile["organizationType"],
  string
> = {
  UNIVERSITY_RESEARCH: "大学 / 科研机构",
  ENTERPRISE: "企业",
  DISTRIBUTOR: "代理商 / 经销商",
  INDIVIDUAL: "个人客户",
  PENDING: "待确认",
};

const qualityLabel: Record<CustomerBusinessProfile["quality"], string> = {
  HIGH: "高",
  RELATIVELY_HIGH: "较高",
  NORMAL: "一般",
  RELATIVELY_LOW: "较低",
  PENDING: "待确认",
};

const controlLabel: Record<ProductRiskProfile["chinaControl"], string> = {
  CLEAR: "可核验资料未提示管控限制",
  RESTRICTED: "已确认存在管控限制",
  PENDING: "缺少管控依据，查货时确认",
};

const yesNoLabel = {
  NO: "当前未发现异常",
  YES: "重点关注",
  PENDING: "缺少资料，查货时确认",
} as const;
const transportLabel: Record<ProductRiskProfile["transportRisk"], string> = {
  NORMAL: "可核验资料未提示特殊运输要求",
  SPECIAL: "查货时确认特殊运输条件",
  DIFFICULT: "查货时确认可用承运渠道",
  UNAVAILABLE: "当前不可运输",
  PENDING: "缺少 SDS / 承运资料，查货时确认",
};

const EMPTY_INQUIRY_HISTORY: InquiryHistorySummary = {
  total: 0,
  last90d: 0,
  last1y: 0,
  sameProduct: 0,
  open: 0,
  lastAt: null,
  items: [],
};

function businessSemanticsOf(
  detail: TriageBusinessWorkbenchDetail,
): TriageBusinessSemantics {
  if (detail.businessSemantics) return detail.businessSemantics;
  const processed = processingResultOf(detail).status === "PROCESSED";
  const serverConfirmedInquiry =
    ["SINGLE_FORWARD", "GROUP_FORWARD"].includes(
      detail.primaryActionCapability.type,
    ) && detail.primaryActionCapability.allowed;
  if (serverConfirmedInquiry || processed) {
    return {
      messageNature: "NEW_INQUIRY",
      messageNatureLabel: "客户询价",
      identity: "CUSTOMER",
      identityLabel: "客户",
      recordKind: "CUSTOMER_INQUIRY",
      recordKindLabel: "客户业务询单",
      queueBucket: processed ? "NO_ACTION_REQUIRED" : "ACTION_REQUIRED",
      queueBucketLabel: processed ? "已处理" : "需要业务处理",
      formalBusinessObject: true,
      communicationRecord: false,
      customerProfileApplicable: true,
      productRiskApplicable: true,
      businessImpactLabel: "业务影响",
      businessImpactSummary: processed
        ? "客户询价已进入案件流程；请查看持久化处理结果。"
        : "服务端主操作已确认该记录可进入销售与查货分发。",
      createsNewCustomer: false,
      createsNewContact: false,
      createsNewInquiry: processed,
      createsNewCase: processed,
      createsNewAssignment: processed,
      createsNewTask: processed,
    };
  }
  return {
    messageNature: "UNKNOWN",
    messageNatureLabel: "业务语义待重新加载",
    identity: "UNKNOWN",
    identityLabel: "身份待确认",
    recordKind: "BUSINESS_REVIEW",
    recordKindLabel: "业务复核记录",
    queueBucket: "EXCEPTION",
    queueBucketLabel: "数据合同异常",
    formalBusinessObject: false,
    communicationRecord: true,
    customerProfileApplicable: false,
    productRiskApplicable: false,
    businessImpactLabel: "暂不可操作",
    businessImpactSummary:
      "当前详情缺少服务端业务语义，已按安全模式关闭客户、风险和分发推断；请刷新后复核。",
    createsNewCustomer: false,
    createsNewContact: false,
    createsNewInquiry: false,
    createsNewCase: false,
    createsNewAssignment: false,
    createsNewTask: false,
  };
}

function allowsHumanNewInquiryConfirmation(
  detail: TriageBusinessWorkbenchDetail,
): boolean {
  const semantics = businessSemanticsOf(detail);
  return (
    detail.subject?.kind === "FORMAL_INQUIRY" &&
    detail.subject.dataOrigin === "REAL_GMAIL" &&
    detail.primaryActionCapability.type === "REVIEW_DECISION" &&
    ["GENERAL_QUESTION", "UNKNOWN"].includes(semantics.messageNature)
  );
}

function primaryActionBusinessLabel(
  detail: TriageBusinessWorkbenchDetail,
): string {
  if (detail.mergedGroup) return "确认联合需求并分发";
  const realGmailInquiry =
    detail.subject?.kind === "FORMAL_INQUIRY" &&
    detail.subject.dataOrigin === "REAL_GMAIL";
  if (
    realGmailInquiry &&
    (detail.primaryActionCapability.type === "SINGLE_FORWARD" ||
      allowsHumanNewInquiryConfirmation(detail))
  ) {
    return "确认客户询单并分发";
  }
  if (
    realGmailInquiry &&
    detail.businessSemantics?.recordKind === "ORDER_REVIEW"
  ) {
    return "提交销售订单复核";
  }
  return detail.primaryActionCapability.label;
}

function customerRelationshipLabel(customer: CustomerBusinessProfile): string {
  if (customer.relationshipStage === "NOT_APPLICABLE") return "不适用";
  if (customer.relationshipStage === "FIRST_INQUIRY") return "首次询客";
  if (customer.relationshipStage === "HISTORICAL_INQUIRER") return "历史询客";
  if (customer.relationshipStage === "CUSTOMER") return "成交客户";
  if (customer.relationshipStage === "ACTIVE_CUSTOMER") return "活跃成交客户";
  return customer.historicalOrderCount > 0
    ? "成交客户"
    : customer.inquiryHistory.total > 0
      ? "历史询客"
      : "首次询客";
}

const AI_STATUS_LABEL: Record<TriageDecisionProfile["analysisStatus"], string> =
  {
    NOT_STARTED: "尚未进行 AI 判断",
    QUEUED: "AI 判断已排队",
    RUNNING: "AI 判断中",
    READY: "AI 判断已完成",
    FAILED: "AI 判断失败，可人工处理",
    STALE: "AI 判断已过期，需重新运行",
  };

const AI_SOURCE_LABEL: Record<TriageDecisionProfile["source"], string> = {
  HARD_FACT: "确定性业务事实",
  EMAIL_INTELLIGENCE: "DeepSeek 邮件智能",
  TRIAGE_AGENT_V1: "DeepSeek / TRIAGE_AGENT_V1",
  SYNTHETIC_FIXTURE: "UAT 固定场景",
  STRUCTURED_EVIDENCE: "结构化证据规则",
  LEGACY_FALLBACK: "历史规则兜底",
  UNKNOWN: "来源待确认",
};

const RISK_STATE_LABEL: Record<
  ProductRiskProfile["riskState"],
  { label: string; tone: string }
> = {
  CONFIRMED_RISK: { label: "已确认存在风险", tone: "confirmed" },
  NO_CONFIRMED_RISK: { label: "当前未发现已确认风险", tone: "clear" },
  INSUFFICIENT_EVIDENCE: { label: "资料不足，待补证", tone: "insufficient" },
};

function businessDisplayNote(value: string | null | undefined): string {
  if (!value) return "";
  if (/合成 UAT|未执行 Provider|REAL-SHADOW|只读影子|只读投影/i.test(value)) {
    return "";
  }
  if (/\b[A-Z][A-Z0-9]+(?:_[A-Z0-9]+)+\b/.test(value)) {
    if (/人工复核/i.test(value)) return "当前记录需要人工复核。";
    if (/阻断|不可承接|拒绝/i.test(value))
      return "已确认存在业务阻断，当前不可承接。";
    return "当前记录包含仅供审计使用的内部标记。";
  }
  return value;
}

export function TriageBusinessWorkbench({
  item,
  detail,
  busy,
  operationState,
  mergeResult,
  distributionResult,
  error,
  notice,
  onClose,
  onSubmit,
  onMarkNotInquiry,
  onMarkInvalidInquiry,
  onMarkHandledElsewhere,
  onNext,
  onRetry,
  onRunAgent,
  onMerge,
  onDismissMerge,
  onLoadOriginalMail,
  onLoadOriginalMailForInquiry,
  onSaveProductFields,
  onSaveCustomerProfile,
  onClearDomainRisk,
  releasedDomainMarks,
  workspaceCode,
}: {
  item:
    | RealShadowInquiry
    | { anonymousReference: string; reviewStatus?: string };
  detail: TriageBusinessWorkbenchDetail;
  busy: boolean;
  operationState:
    | "LOADING"
    | "LOAD_FAILED"
    | "READY"
    | "CONFIRMING"
    | "SUBMITTING"
    | "MERGED_SUCCESS"
    | "DISTRIBUTED_SUCCESS"
    | "STALE";
  mergeResult?: RealShadowMergeResult;
  distributionResult?:
    | RealShadowDistributionResult
    | RealShadowGroupDistributionResult
    | FormalInquiryDistributionResult;
  error: string;
  notice: string;
  onClose: () => void;
  onSubmit: (
    salesUserId?: string,
    sourcingUserId?: string,
    sourcingStrategy?: HumanGmailSourcingStrategy,
    confirmedFields?: ConfirmedInquiryFields,
    confirmedMessageNature?: ConfirmedMessageNature,
    ccSalesArchive?: boolean,
  ) => void;
  onMarkNotInquiry?: (suppressSender: boolean) => void;
  onMarkInvalidInquiry?: (reason: string, suppressSender: boolean) => void;
  onMarkHandledElsewhere?: (reason: string) => void;
  onNext: () => void;
  onRetry: () => void;
  onRunAgent?: () => void;
  onMerge: (candidateSnapshotIds: string[], actionVersion?: string) => void;
  onDismissMerge: (
    candidateSnapshotIds: string[],
    actionVersion?: string,
  ) => void;
  onLoadOriginalMail?: () => Promise<OriginalMailMessage | null>;
  onLoadOriginalMailForInquiry?: (
    inquiryId: string,
  ) => Promise<OriginalMailMessage | null>;
  onSaveProductFields?: (
    fields: ConfirmedInquiryFields,
    reason?: string,
  ) => Promise<void>;
  onSaveCustomerProfile?: (
    profile: CustomerProfileDraft,
    reason?: string,
  ) => Promise<void>;
  /** Admin-only release of a permanent domain-risk mark; absent when the caller cannot clear it. */
  onClearDomainRisk?: (domain: string) => Promise<void>;
  /**
   * Domains whose permanent Workspace mark this operator released during this session, keyed by
   * normalised domain. The mark itself lives in the Workspace register; this only lets the card
   * show the operator that the release happened instead of looking like an unresponsive button.
   */
  releasedDomainMarks?: Record<string, string>;
  workspaceCode?: string;
}) {
  const [peek, setPeek] = useState<Peek>(() => peekFromUrl());
  const [originalMailOpen, setOriginalMailOpen] = useState(false);
  const [originalMail, setOriginalMail] = useState<OriginalMailMessage | null>(
    null,
  );
  const [originalMailInquiryId, setOriginalMailInquiryId] = useState<
    string | null
  >(null);
  const [originalMailLoading, setOriginalMailLoading] = useState(false);
  const [productEditorOpen, setProductEditorOpen] = useState(false);
  const [customerEditorOpen, setCustomerEditorOpen] = useState(false);
  const [editorBusy, setEditorBusy] = useState(false);
  const [editorError, setEditorError] = useState("");
  const [forwardConfirmOpen, setForwardConfirmOpen] = useState(false);
  const [notInquiryConfirmOpen, setNotInquiryConfirmOpen] = useState(false);
  const [invalidInquiryConfirmOpen, setInvalidInquiryConfirmOpen] =
    useState(false);
  const [invalidInquiryReason, setInvalidInquiryReason] =
    useState<InvalidInquiryReason>("SPAM_OR_MALICIOUS");
  const [notInquirySuppressSender, setNotInquirySuppressSender] =
    useState(false);
  const [invalidInquirySuppressSender, setInvalidInquirySuppressSender] =
    useState(false);
  const [handledElsewhereConfirmOpen, setHandledElsewhereConfirmOpen] =
    useState(false);
  const [handledElsewhereReason, setHandledElsewhereReason] =
    useState<HandledElsewhereReason>("HANDLED_IN_GMAIL");
  const [teamConversionSubmitState, setTeamConversionSubmitState] = useState({
    inquiryId: "",
    canSubmit: false,
    busy: false,
  });
  const [mergeConfirmRequest, setMergeConfirmRequest] = useState<{
    candidateSnapshotIds: string[];
    actionVersion?: string;
  } | null>(null);
  const [crossWorkspaceDuplicates, setCrossWorkspaceDuplicates] =
    useState<CrossWorkspaceDuplicateResponse | null>(null);
  const [crossWorkspaceLoading, setCrossWorkspaceLoading] = useState(false);
  const [crossWorkspaceError, setCrossWorkspaceError] = useState("");
  const [crossWorkspaceAcknowledged, setCrossWorkspaceAcknowledged] =
    useState(false);
  const [selectedSalesUserId, setSelectedSalesUserId] = useState(
    () => detail.distributionDecision.salesCandidate?.userId ?? "",
  );
  const [selectedSourcingUserId, setSelectedSourcingUserId] = useState(
    () => detail.distributionDecision.sourcingCandidate?.userId ?? "",
  );
  // Transition-period convenience: the operator may drop the shared sales archive copy so trial
  // distributions stop mailing the sales operations mailbox. It stays checked by default.
  const [ccSalesArchive, setCcSalesArchive] = useState(true);
  const [selectedSourcingStrategy, setSelectedSourcingStrategy] =
    useState<HumanGmailSourcingStrategy>(() => defaultSourcingStrategy(detail));
  const [selectedGroupRiskId, setSelectedGroupRiskId] = useState<string | null>(
    null,
  );
  const inquiry = detail.inquiryDetailProfile;
  const [confirmedFields, setConfirmedFields] =
    useState<ConfirmedInquiryFields>(() => confirmedFieldsFromInquiry(inquiry));
  const customer = detail.customerBusinessProfile;
  const [customerDraft, setCustomerDraft] = useState<CustomerProfileDraft>(() =>
    customerProfileDraftFrom(customer),
  );
  const businessSemantics = businessSemanticsOf(detail);
  const communicationOnly =
    businessSemantics.recordKind === "COMMUNICATION_ONLY";
  const reviewOnlyRecord = !businessSemantics.formalBusinessObject;
  const canonicalInquiryCount =
    detail.inquiries?.length ?? detail.subject?.memberCount ?? 1;
  const canonicalProductCount =
    detail.products?.length ?? detail.subject?.productCount ?? 1;
  const inquiryHistory = customer.inquiryHistory ?? EMPTY_INQUIRY_HISTORY;
  const risk = detail.productRiskProfile;
  // Loaded once per workbench subject and rendered in both the customer profile and the product
  // reference section; the archive never blocks the triage detail.
  const mboxHistory = useMboxHistory(
    detail.subject
      ? { kind: detail.subject.kind, id: detail.subject.id }
      : undefined,
  );
  // 历史库等级只是内部留证，和当前判断一致时当脚注，不一致时才需要分诊员看到。
  const archiveGrade = archiveGradeSuggestion(
    mboxHistory.state,
    customerDraft.customerGrade,
  );
  const distribution = detail.distributionDecision;
  const salesCandidates = distribution.salesCandidates?.length
    ? distribution.salesCandidates
    : distribution.salesCandidate
      ? [distribution.salesCandidate]
      : [];
  const sourcingCandidates = distribution.sourcingCandidates?.length
    ? distribution.sourcingCandidates
    : distribution.sourcingCandidate
      ? [distribution.sourcingCandidate]
      : [];
  const salesCandidateIds = salesCandidates
    .map((candidate) => candidate.userId)
    .join("|");
  const sourcingCandidateIds = sourcingCandidates
    .map((candidate) => candidate.userId)
    .join("|");
  const preferredSalesUserId =
    distribution.salesCandidate?.userId ?? salesCandidates[0]?.userId ?? "";
  const preferredSourcingUserId =
    distribution.sourcingCandidate?.userId ??
    sourcingCandidates[0]?.userId ??
    "";
  useEffect(() => {
    setSelectedSalesUserId((current) =>
      current && salesCandidateIds.split("|").includes(current)
        ? current
        : preferredSalesUserId,
    );
  }, [preferredSalesUserId, salesCandidateIds]);
  useEffect(() => {
    setSelectedSourcingUserId((current) =>
      current && sourcingCandidateIds.split("|").includes(current)
        ? current
        : preferredSourcingUserId,
    );
  }, [preferredSourcingUserId, sourcingCandidateIds]);
  const primaryAction = detail.primaryActionCapability;
  const primaryActionLabel = primaryActionBusinessLabel(detail);
  const confirmationActionLabel = primaryActionLabel.startsWith("确认")
    ? primaryActionLabel
    : `确认${primaryActionLabel}`;
  const realGmailFormal =
    detail.subject?.kind === "FORMAL_INQUIRY" &&
    detail.subject.dataOrigin === "REAL_GMAIL";
  const crossWorkspaceInquiryId =
    detail.subject?.kind === "FORMAL_INQUIRY" ? detail.subject.id : null;
  const humanNewInquiryConfirmation = allowsHumanNewInquiryConfirmation(detail);
  const orderReview = detail.emailIntelligence?.orderReview;
  const isOrderReview =
    businessSemantics.recordKind === "ORDER_REVIEW" ||
    orderReview?.isOrderCandidate === true;
  const mergeCandidates = detail.duplicateProfile.mergeCandidates ?? [];
  const processingResult = processingResultOf(detail);
  const processed =
    primaryAction.type === "VIEW_RESULT" ||
    processingResult.status === "PROCESSED";
  const teamConversionInquiryId =
    !processed && detail.subject?.kind === "FORMAL_INQUIRY" && !isOrderReview
      ? detail.subject.id
      : undefined;
  const teamConversionFormId = teamConversionInquiryId
    ? `team-conversion-${teamConversionInquiryId}`
    : undefined;
  const decision = triageDecisionProfileOf(detail);
  const selectedGroupRisk = detail.mergedGroup?.items.find(
    (member) => member.snapshotId === selectedGroupRiskId,
  );
  const openCase = (caseId: string | null | undefined) => {
    if (caseId)
      window.location.assign(
        `/?view=inquiry-center&case=${encodeURIComponent(caseId)}`,
      );
  };
  const openForwardConfirm = () => {
    setCrossWorkspaceLoading(Boolean(crossWorkspaceInquiryId));
    setForwardConfirmOpen(true);
  };
  const openMergeConfirm = (
    candidateSnapshotIds: string[],
    actionVersion?: string,
  ) => {
    if (!candidateSnapshotIds.length) return;
    setMergeConfirmRequest({ candidateSnapshotIds, actionVersion });
  };
  const gasPrecheckPending = detail.gasPrecheck?.status === "PENDING";
  const confirmationSummary = detail.mergedGroup
    ? realGmailFormal
      ? `将 ${detail.mergedGroup.inquiryCount} 条询单作为一个联合需求关联至同一案件，并交给销售与查货共同处理；原始邮件、产品信息和判断依据继续分别保留。`
      : `将 ${primaryAction.objectCount} 个可处理产品关联至 1 个案件，并建立 1 个销售分配、1 个查货分配和 ${primaryAction.objectCount} 个产品任务。`
    : humanNewInquiryConfirmation
      ? isOrderReview
        ? "确认后会将采购订单候选提交给销售复核，并保留 DeepSeek 原始判断与人工复核记录。"
        : "人工确认后，系统会将该邮件更正为客户新询价，并创建 1 个案件、1 个销售责任、1 个查货责任及对应任务。AI 原始判断与人工更正记录都会保留。"
      : primaryAction.type === "REVIEW_DECISION"
        ? `将按“${primaryActionLabel}”记录当前人工处置；只有分发类决定才会创建案件、分配或任务。`
        : primaryAction.type === "HANDLE_DUPLICATE"
          ? "将按当前重复关系证据记录人工处理结果，不会自动覆盖原始询单。"
          : "将当前询单进入案件流程，并按页面所示销售与查货候选建立分配。";
  const crossWorkspaceRequiresAcknowledgement = Boolean(
    crossWorkspaceDuplicates?.matches.some(
      ({ distributionStatus }) => distributionStatus === "DISTRIBUTED",
    ),
  );
  const crossWorkspaceCheckPending =
    forwardConfirmOpen && crossWorkspaceLoading;
  useEffect(() => {
    const restorePeek = () => {
      setPeek(peekFromUrl());
    };
    window.addEventListener("popstate", restorePeek);
    return () => window.removeEventListener("popstate", restorePeek);
  }, []);
  useEffect(() => {
    setPeek(peekFromUrl());
    setForwardConfirmOpen(false);
    setNotInquiryConfirmOpen(false);
    setInvalidInquiryConfirmOpen(false);
    setInvalidInquiryReason("SPAM_OR_MALICIOUS");
    setNotInquirySuppressSender(false);
    setInvalidInquirySuppressSender(false);
    setHandledElsewhereConfirmOpen(false);
    setHandledElsewhereReason("HANDLED_IN_GMAIL");
    setMergeConfirmRequest(null);
    setSelectedGroupRiskId(null);
    setSelectedSalesUserId(
      detail.distributionDecision.salesCandidate?.userId ?? "",
    );
    setSelectedSourcingUserId(
      detail.distributionDecision.sourcingCandidate?.userId ?? "",
    );
    setSelectedSourcingStrategy(defaultSourcingStrategy(detail));
    setConfirmedFields(confirmedFieldsFromInquiry(detail.inquiryDetailProfile));
    setCustomerDraft(customerProfileDraftFrom(detail.customerBusinessProfile));
    setOriginalMailOpen(false);
    setOriginalMail(null);
    setOriginalMailInquiryId(null);
    setProductEditorOpen(false);
    setCustomerEditorOpen(false);
    setEditorError("");
  }, [item.anonymousReference]);
  useEffect(() => {
    if (!forwardConfirmOpen || !crossWorkspaceInquiryId) {
      setCrossWorkspaceDuplicates(null);
      setCrossWorkspaceLoading(false);
      setCrossWorkspaceError("");
      setCrossWorkspaceAcknowledged(false);
      return;
    }
    let cancelled = false;
    setCrossWorkspaceLoading(true);
    setCrossWorkspaceError("");
    setCrossWorkspaceAcknowledged(false);
    void loadCrossWorkspaceDuplicates(crossWorkspaceInquiryId)
      .then((result) => {
        if (!cancelled) setCrossWorkspaceDuplicates(result);
      })
      .catch(() => {
        if (cancelled) return;
        setCrossWorkspaceDuplicates(null);
        setCrossWorkspaceError(
          "跨空间重复检查暂不可用，请分发前人工核对其他工作空间。",
        );
      })
      .finally(() => {
        if (!cancelled) setCrossWorkspaceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [crossWorkspaceInquiryId, forwardConfirmOpen]);
  useEffect(() => {
    if (
      !forwardConfirmOpen &&
      !notInquiryConfirmOpen &&
      !invalidInquiryConfirmOpen &&
      !handledElsewhereConfirmOpen &&
      !mergeConfirmRequest &&
      !originalMailOpen &&
      !productEditorOpen &&
      !customerEditorOpen
    )
      return;
    const closeConfirmation = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (originalMailOpen) {
        setOriginalMailOpen(false);
        return;
      }
      setForwardConfirmOpen(false);
      setNotInquiryConfirmOpen(false);
      setInvalidInquiryConfirmOpen(false);
      setHandledElsewhereConfirmOpen(false);
      setMergeConfirmRequest(null);
      setOriginalMailOpen(false);
      setProductEditorOpen(false);
      setCustomerEditorOpen(false);
    };
    window.addEventListener("keydown", closeConfirmation, true);
    return () => window.removeEventListener("keydown", closeConfirmation, true);
  }, [
    forwardConfirmOpen,
    invalidInquiryConfirmOpen,
    handledElsewhereConfirmOpen,
    mergeConfirmRequest,
    notInquiryConfirmOpen,
    originalMailOpen,
    productEditorOpen,
    customerEditorOpen,
  ]);
  const openPeek = (next: Exclude<Peek, null>) => {
    const url = new URL(window.location.href);
    url.searchParams.set("peek", next.toLowerCase());
    window.history[peek ? "replaceState" : "pushState"](null, "", url);
    setPeek(next);
  };

  const openOriginalMail = async (inquiryId?: string) => {
    const targetInquiryId = inquiryId ?? null;
    const cachedForTarget = originalMailInquiryId === targetInquiryId;
    setOriginalMailOpen(true);
    if (originalMail && cachedForTarget) return;
    const loader = targetInquiryId
      ? onLoadOriginalMailForInquiry
      : onLoadOriginalMail;
    if (!loader) return;
    setOriginalMailInquiryId(targetInquiryId);
    setOriginalMail(null);
    setOriginalMailLoading(true);
    setEditorError("");
    try {
      setOriginalMail(
        targetInquiryId
          ? await loader(targetInquiryId)
          : await (loader as () => Promise<OriginalMailMessage | null>)(),
      );
    } catch (cause) {
      setEditorError(
        describeActionError(cause, "邮件原文加载失败，请稍后重试。"),
      );
    } finally {
      setOriginalMailLoading(false);
    }
  };

  const saveProductFields = async (
    next: ConfirmedInquiryFields,
    reason?: string,
  ) => {
    setEditorBusy(true);
    setEditorError("");
    setConfirmedFields(next);
    try {
      if (onSaveProductFields) await onSaveProductFields(next, reason);
      setProductEditorOpen(false);
    } catch (cause) {
      setEditorError(describeActionError(cause, "产品信息保存失败。"));
    } finally {
      setEditorBusy(false);
    }
  };

  const saveCustomerProfile = async (
    next: CustomerProfileDraft,
    reason?: string,
  ) => {
    setEditorBusy(true);
    setEditorError("");
    setCustomerDraft(next);
    try {
      if (onSaveCustomerProfile) await onSaveCustomerProfile(next, reason);
      setCustomerEditorOpen(false);
    } catch (cause) {
      setEditorError(describeActionError(cause, "客户画像保存失败。"));
    } finally {
      setEditorBusy(false);
    }
  };
  const closePeek = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("peek")) {
      url.searchParams.delete("peek");
      window.history.replaceState(null, "", url);
    }
    setPeek(null);
  };
  useEffect(() => {
    if (!peek) return;
    const closeTopLayer = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      closePeek();
    };
    window.addEventListener("keydown", closeTopLayer);
    return () => window.removeEventListener("keydown", closeTopLayer);
  });
  const overlayRoot = document.getElementById("app-overlay-root");
  if (!overlayRoot) throw new Error("缺少应用 Overlay Root");
  const peekOverlay = peek
    ? createPortal(
        <WorkbenchSecondaryPanelShell
          title={peekTitle(peek)}
          onClose={closePeek}
        >
          {peek === "CUSTOMER" &&
          businessSemantics.customerProfileApplicable ? (
            <CustomerSources customer={customer} />
          ) : null}
          {peek === "MAIL" ? (
            <MailJudgmentPeek judgment={detail.mailJudgment} />
          ) : null}
          {peek === "HISTORY" ? (
            <HistoryPeek history={inquiryHistory} distribution={distribution} />
          ) : null}
          {peek === "RISK" && businessSemantics.productRiskApplicable ? (
            <RiskPeek
              productName={
                selectedGroupRisk?.product ?? confirmedFields.productName
              }
              cas={selectedGroupRisk?.cas ?? confirmedFields.cas}
              risk={selectedGroupRisk?.productRiskProfile ?? risk}
            />
          ) : null}
          {peek === "DUPLICATE" ? (
            <DuplicatePeek profile={detail.duplicateProfile} />
          ) : null}
        </WorkbenchSecondaryPanelShell>,
        overlayRoot,
      )
    : null;
  const modalOverlays = createPortal(
    <>
      {originalMailOpen ? (
        <MailOriginalModal
          evidence={originalMailInquiryId ? null : detail.mailEvidence}
          originalMail={originalMail}
          inquiryId={
            detail.subject?.kind === "FORMAL_INQUIRY"
              ? (originalMailInquiryId ?? detail.subject.id)
              : null
          }
          loading={originalMailLoading}
          error={editorError}
          onClose={() => setOriginalMailOpen(false)}
          onRetry={() =>
            void openOriginalMail(originalMailInquiryId ?? undefined)
          }
        />
      ) : null}
      {productEditorOpen ? (
        <ProductInformationEditor
          initial={confirmedFields}
          busy={editorBusy}
          error={editorError}
          onClose={() => setProductEditorOpen(false)}
          onSave={(next, reason) => void saveProductFields(next, reason)}
        />
      ) : null}
      {customerEditorOpen ? (
        <CustomerProfileEditor
          initial={customerDraft}
          busy={editorBusy}
          error={editorError}
          onClose={() => setCustomerEditorOpen(false)}
          onSave={(next, reason) => void saveCustomerProfile(next, reason)}
        />
      ) : null}
    </>,
    overlayRoot,
  );
  return (
    <main className="real-business-workbench" aria-label="询单分诊工作台">
      <header className="real-business-header">
        <div className="real-header-context">
          <h1>
            {businessSemantics.customerProfileApplicable
              ? (customerDraft.organizationName ?? "客户主体待确认")
              : businessSemantics.identityLabel}
          </h1>
          <p>
            {detail.subject?.displayReference ?? item.anonymousReference} ·{" "}
            {detail.subject?.dataOrigin === "REAL_GMAIL"
              ? "来源：真实 Gmail"
              : detail.subject?.dataOrigin === "SYNTHETIC"
                ? "测试数据"
                : "业务来源记录"}
            {detail.lifecycle
              ? ` · ${
                  detail.lifecycle.status === "PROCESSED"
                    ? "已处理"
                    : detail.lifecycle.status === "REVIEW_REQUIRED"
                      ? "待复核"
                      : "待处理"
                }`
              : ""}
            {canonicalInquiryCount > 1
              ? ` · ${canonicalInquiryCount} 条询单 · ${canonicalProductCount} 个产品`
              : ""}
          </p>
        </div>
      </header>

      {notice ? <div className="triage-feedback success">{notice}</div> : null}
      {error ? <div className="triage-feedback error">{error}</div> : null}
      {operationState === "DISTRIBUTED_SUCCESS" && distributionResult ? (
        <DistributionSuccessResult
          result={distributionResult}
          detail={detail}
          onOpenCase={openCase}
          onNext={onNext}
          onClose={onClose}
        />
      ) : null}
      {operationState === "STALE" ? (
        <div className="real-operation-stale" role="alert">
          <strong>页面状态已变化</strong>
          <p>其他操作已改变当前询单，请刷新详情后重新确认。</p>
          <button type="button" onClick={onRetry}>
            刷新当前询单
          </button>
        </div>
      ) : null}
      {gasPrecheckPending ? (
        <div className="real-operation-result is-pending" role="status">
          <strong>已创建气态属性确认任务</strong>
          <p>已转交查货确认气态属性，确认后将恢复普通分发或保持不可承接。</p>
          <p>
            查货负责人：{candidateLabel(sourcingCandidates[0] ?? null)} ·
            任务状态：待确认
          </p>
          <p>任务编号：{detail.gasPrecheck?.taskId ?? "已创建"}</p>
        </div>
      ) : null}

      <div className="real-business-content">
        <section
          className="real-business-section"
          aria-labelledby="inquiry-detail-title"
        >
          <div className="real-business-section-heading">
            <div>
              <h2 id="inquiry-detail-title">
                {detail.mergedGroup
                  ? "联合需求"
                  : communicationOnly
                    ? "通信内容"
                    : reviewOnlyRecord &&
                        businessSemantics.recordKind === "ORDER_REVIEW"
                      ? "订单复核内容"
                      : reviewOnlyRecord && !humanNewInquiryConfirmation
                        ? "业务沟通内容"
                        : "询单与需求"}
              </h2>
            </div>
          </div>
          {detail.mailEvidence ? (
            <MailEvidencePanel
              evidence={detail.mailEvidence}
              judgment={detail.mailJudgment}
              onOpenEvidence={() => openPeek("MAIL")}
              onOpenOriginal={() => void openOriginalMail()}
            />
          ) : null}
          {detail.mergedGroup ? (
            <>
              <MergedGroupRequirement
                group={detail.mergedGroup}
                customerCountry={customer.country}
                onOpenRisk={(snapshotId) => {
                  setSelectedGroupRiskId(snapshotId);
                  openPeek("RISK");
                }}
              />
              <MboxHistorySection
                state={mboxHistory.state}
                onReload={mboxHistory.reload}
              />
            </>
          ) : reviewOnlyRecord && !humanNewInquiryConfirmation ? (
            <>
              {!detail.mailEvidence ? (
                <CustomerMessage inquiry={inquiry} label="邮件内容" />
              ) : null}
            </>
          ) : (
            <ProductEvidenceCard
              inquiry={inquiry}
              confirmedFields={confirmedFields}
              risk={risk}
              history={detail.productCommercialHistory}
              showCustomerMessage={!detail.mailEvidence}
              onOpenRisk={() => openPeek("RISK")}
              onOpenEditor={() => setProductEditorOpen(true)}
              fieldStates={detail.products?.[0]?.fieldStates}
              humanOverride={detail.products?.[0]?.humanOverride}
              archiveState={mboxHistory.state}
              onReloadArchive={mboxHistory.reload}
            />
          )}
          {distribution.linkedCase && !detail.mergedGroup ? (
            <ExistingCaseEvidence
              linkedCase={distribution.linkedCase}
              historicalOnly={communicationOnly}
              onOpenCase={openCase}
            />
          ) : null}
          {!communicationOnly &&
          detail.duplicateProfile.duplicateCandidates.length ? (
            <div className="real-business-note is-duplicate">
              <strong>{detail.duplicateProfile.summary}</strong>
              <button type="button" onClick={() => openPeek("DUPLICATE")}>
                查看询单关系与差异
              </button>
            </div>
          ) : null}
          {!communicationOnly && mergeCandidates.length ? (
            <div className="real-inline-merge-review">
              <header>
                <div>
                  <strong>待合并询单</strong>
                  <span>建议合并 {mergeCandidates.length + 1} 条询单</span>
                </div>
                <div>
                  <button
                    type="button"
                    className="real-secondary-action"
                    disabled={busy}
                    onClick={() =>
                      onDismissMerge(
                        mergeCandidates.map(
                          (candidate) => candidate.snapshotId,
                        ),
                        primaryAction.actionVersion,
                      )
                    }
                  >
                    分别处理
                  </button>
                  <button
                    type="button"
                    className="triage-primary-action"
                    disabled={busy}
                    onClick={() =>
                      openMergeConfirm(
                        mergeCandidates.map(
                          (candidate) => candidate.snapshotId,
                        ),
                        primaryAction.actionVersion,
                      )
                    }
                  >
                    合并处理
                  </button>
                </div>
              </header>
              <p>
                {detail.duplicateProfile.mergeSuggestion?.reason ??
                  "同一客户在 12 小时内提交不同产品询单，建议合并为联合需求。"}
              </p>
              <div className="real-inline-merge-list">
                {mergeCandidates.map((candidate) => (
                  <article key={candidate.snapshotId}>
                    <div>
                      <strong>{candidate.product ?? "产品待确认"}</strong>
                      <span>
                        {candidate.cas ? `CAS ${candidate.cas} · ` : ""}
                        {candidate.quantity ?? "数量待确认"}
                      </span>
                    </div>
                    <small>
                      {candidate.reasons?.[0] ??
                        "同一客户在合并窗口内提交，建议作为同一次需求处理。"}
                    </small>
                    <button
                      type="button"
                      className="real-secondary-action"
                      disabled={busy || !candidate.mergeActionVersion}
                      onClick={() =>
                        openMergeConfirm(
                          [candidate.snapshotId],
                          candidate.mergeActionVersion,
                        )
                      }
                    >
                      与当前询单合并
                    </button>
                  </article>
                ))}
              </div>
            </div>
          ) : null}
          {operationState === "MERGED_SUCCESS" && mergeResult ? (
            <div className="real-operation-result is-merged" role="status">
              <span>合并完成</span>
              <strong>已合并为一个联合需求</strong>
              <p>
                {mergeResult.sourceSnapshotCount} 条询单 ·{" "}
                {detail.mergedGroup?.productCount ??
                  mergeResult.sourceSnapshotCount}{" "}
                个产品
              </p>
              <p>每条原始询单及其邮件、产品和风险依据都继续保留。</p>
            </div>
          ) : null}
          {mergeConfirmRequest ? (
            <div
              className="real-merge-confirm real-forward-confirm"
              data-overlay-layer="action-confirmation"
              role="dialog"
              aria-modal="true"
              aria-hidden={originalMailOpen ? "true" : undefined}
              aria-label={`确认合并 ${mergeConfirmRequest.candidateSnapshotIds.length + 1} 条询单`}
            >
              <strong>
                确认合并 {mergeConfirmRequest.candidateSnapshotIds.length + 1}{" "}
                条询单
              </strong>
              <p>
                确认后，这些询单会进入同一个联合需求；每条原始询单及其业务依据仍分别保留。
              </p>
              <div className="real-merge-confirm-list">
                <MergeConfirmationItem
                  inquiryId={detail.subject?.id ?? null}
                  product={
                    detail.products?.[0]?.name ??
                    detail.inquiryDetailProfile.productName
                  }
                  cas={
                    detail.products?.[0]?.cas ?? detail.inquiryDetailProfile.cas
                  }
                  quantity={
                    detail.products?.[0]?.quantity ??
                    detail.inquiryDetailProfile.quantity
                  }
                  submittedAt={detail.subject?.submittedAt ?? null}
                  current
                  onOpenOriginalMail={
                    onLoadOriginalMailForInquiry
                      ? (inquiryId) => void openOriginalMail(inquiryId)
                      : undefined
                  }
                />
                {mergeCandidates
                  .filter((candidate) =>
                    mergeConfirmRequest.candidateSnapshotIds.includes(
                      candidate.snapshotId,
                    ),
                  )
                  .map((candidate) => (
                    <MergeConfirmationItem
                      key={candidate.snapshotId}
                      inquiryId={candidate.snapshotId}
                      product={candidate.product}
                      cas={candidate.cas}
                      quantity={candidate.quantity}
                      submittedAt={candidate.submittedAt}
                      onOpenOriginalMail={
                        onLoadOriginalMailForInquiry
                          ? (inquiryId) => void openOriginalMail(inquiryId)
                          : undefined
                      }
                    />
                  ))}
              </div>
              <div>
                <button
                  type="button"
                  className="real-secondary-action"
                  onClick={() => setMergeConfirmRequest(null)}
                  disabled={busy}
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const request = mergeConfirmRequest;
                    setMergeConfirmRequest(null);
                    onMerge(
                      request.candidateSnapshotIds,
                      request.actionVersion,
                    );
                  }}
                  disabled={busy || !primaryAction.allowed}
                >
                  确认合并 {mergeConfirmRequest.candidateSnapshotIds.length + 1}{" "}
                  条询单
                </button>
              </div>
            </div>
          ) : null}
          {forwardConfirmOpen &&
          (detail.subject?.kind !== "FORMAL_INQUIRY" || isOrderReview) ? (
            <div
              className="real-merge-confirm real-forward-confirm"
              data-overlay-layer="action-confirmation"
              role="dialog"
              aria-modal="true"
              aria-label={
                isOrderReview
                  ? "提交销售订单复核"
                  : realGmailFormal
                    ? "确认客户询单并分发"
                    : confirmationActionLabel
              }
            >
              <strong>
                {isOrderReview
                  ? "提交销售订单复核"
                  : realGmailFormal
                    ? "确认客户询单并分发"
                    : confirmationActionLabel}
              </strong>
              <p>{confirmationSummary}</p>
              <CrossWorkspaceDuplicateNotice
                result={crossWorkspaceDuplicates}
                loading={crossWorkspaceLoading}
                error={crossWorkspaceError}
                acknowledged={crossWorkspaceAcknowledged}
                onAcknowledgedChange={setCrossWorkspaceAcknowledged}
              />
              {isOrderReview ? (
                <p>
                  销售负责人会收到订单复核任务；不会创建查货任务，不会向客户或供应商外发。
                </p>
              ) : realGmailFormal ? (
                <>
                  <p>
                    当前人工确认会创建客户业务案件，并同时建立销售与查货责任。
                    查货策略：
                    {selectedSourcingStrategy === "REVIEW_REUSABLE_HISTORY"
                      ? "复核历史供货信息"
                      : "重新查货"}
                    。
                  </p>
                  <p>
                    系统会向已配置的销售与查货测试邮箱发送内部任务通知，同时保留
                    Mailpit
                    留证；不会向客户或供应商发送邮件，只会把这封询单对应的 Gmail
                    原始邮件标记为已读。
                  </p>
                </>
              ) : (
                <p>
                  此次操作只建立内部案件、分配与任务，不向客户或供应商发送邮件。
                </p>
              )}
              <div>
                <button
                  type="button"
                  className="real-secondary-action"
                  onClick={() => setForwardConfirmOpen(false)}
                  disabled={busy}
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setForwardConfirmOpen(false);
                    onSubmit(
                      selectedSalesUserId || undefined,
                      selectedSourcingUserId || undefined,
                      realGmailFormal ? selectedSourcingStrategy : undefined,
                      confirmedFields,
                      isOrderReview ? "PURCHASE_ORDER" : "NEW_INQUIRY",
                      ccSalesArchive,
                    );
                  }}
                  disabled={
                    busy ||
                    !primaryAction.allowed ||
                    crossWorkspaceCheckPending ||
                    (crossWorkspaceRequiresAcknowledgement &&
                      !crossWorkspaceAcknowledged)
                  }
                >
                  {isOrderReview
                    ? "提交销售订单复核"
                    : realGmailFormal
                      ? "确认客户询单并分发"
                      : confirmationActionLabel}
                </button>
              </div>
            </div>
          ) : null}
          {notInquiryConfirmOpen ? (
            <div
              className="real-merge-confirm real-forward-confirm"
              data-overlay-layer="action-confirmation"
              role="dialog"
              aria-modal="true"
              aria-label="确认不是客户询单"
            >
              <strong>确认不是客户询单</strong>
              <p>
                {detail.mailJudgment?.summary ??
                  "当前邮件性质尚未完成结构化判断，请确认它不是客户新询价。"}
              </p>
              <p>
                主题：{detail.mailEvidence?.subject ?? "主题待确认"} · 发件人：
                {mailAddressLabel(detail.mailEvidence?.sender)}
              </p>
              <p>
                当前邮件将关闭为无需创建客户案件的记录，不会创建案件、分配或任务，也不会向客户或供应商发送邮件；系统只会把这封询单对应的
                Gmail 原始邮件标记为已读。
              </p>
              <label className="real-suppress-sender-option">
                <input
                  type="checkbox"
                  checked={notInquirySuppressSender}
                  onChange={(event) =>
                    setNotInquirySuppressSender(event.target.checked)
                  }
                  disabled={busy}
                />
                <span>
                  同时加入不再合作名单：该发件人之后的询单会自动判为无效，不再需要人工重复操作
                </span>
              </label>
              <div>
                <button
                  type="button"
                  className="real-secondary-action"
                  onClick={() => setNotInquiryConfirmOpen(false)}
                  disabled={busy}
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNotInquiryConfirmOpen(false);
                    onMarkNotInquiry?.(notInquirySuppressSender);
                  }}
                  disabled={busy || !onMarkNotInquiry}
                >
                  确认不是客户询单
                </button>
              </div>
            </div>
          ) : null}
          {invalidInquiryConfirmOpen ? (
            <div
              className="real-merge-confirm real-forward-confirm real-invalid-inquiry-confirm"
              data-overlay-layer="action-confirmation"
              role="dialog"
              aria-modal="true"
              aria-label="确认无效询单"
            >
              <strong>确认无效询单</strong>
              <p>
                当前记录仍保留为客户询单，仅因质量或有效性原因关闭；邮件判断、客户画像、产品与风险证据不会被改写。
              </p>
              <p>
                关闭后不会创建案件、分配或任务，也不会向客户或供应商发送邮件；系统只会把这封询单对应的
                Gmail 原始邮件标记为已读。
              </p>
              <label>
                <span>无效原因</span>
                <select
                  aria-label="无效原因"
                  value={invalidInquiryReason}
                  onChange={(event) =>
                    setInvalidInquiryReason(
                      event.target.value as InvalidInquiryReason,
                    )
                  }
                  disabled={busy}
                >
                  {Object.entries(invalidInquiryReasons).map(
                    ([value, label]) => (
                      <option value={value} key={value}>
                        {label}
                      </option>
                    ),
                  )}
                </select>
              </label>
              <label className="real-suppress-sender-option">
                <input
                  type="checkbox"
                  checked={invalidInquirySuppressSender}
                  onChange={(event) =>
                    setInvalidInquirySuppressSender(event.target.checked)
                  }
                  disabled={busy}
                />
                <span>
                  同时加入不再合作名单：该发件人之后的询单会自动判为无效，不再需要人工重复操作
                </span>
              </label>
              <div>
                <button
                  type="button"
                  className="real-secondary-action"
                  onClick={() => setInvalidInquiryConfirmOpen(false)}
                  disabled={busy}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="real-invalid-inquiry-confirm-action"
                  onClick={() => {
                    setInvalidInquiryConfirmOpen(false);
                    onMarkInvalidInquiry?.(
                      invalidInquiryReasons[invalidInquiryReason],
                      invalidInquirySuppressSender,
                    );
                  }}
                  disabled={busy || !onMarkInvalidInquiry}
                >
                  确认无效询单
                </button>
              </div>
            </div>
          ) : null}
          {handledElsewhereConfirmOpen ? (
            <div
              className="real-merge-confirm real-forward-confirm real-handled-elsewhere-confirm"
              data-overlay-layer="action-confirmation"
              role="dialog"
              aria-modal="true"
              aria-label="确认已在别处处理"
            >
              <strong>确认已在 Gmail/他处处理</strong>
              <p>
                当前记录仍保留为客户询单，仅在 CRM
                内关闭；邮件判断、客户画像、产品与风险证据不会被改写。
              </p>
              <p>
                关闭后不会创建案件、分配或任务，也不会向客户或供应商发送邮件；与“无效询单”不同，系统不会修改
                Gmail 里这封邮件的已读状态，原来的 Gmail 工作流不受影响。
              </p>
              <label>
                <span>处理去向</span>
                <select
                  aria-label="处理去向"
                  value={handledElsewhereReason}
                  onChange={(event) =>
                    setHandledElsewhereReason(
                      event.target.value as HandledElsewhereReason,
                    )
                  }
                  disabled={busy}
                >
                  {Object.entries(handledElsewhereReasons).map(
                    ([value, label]) => (
                      <option value={value} key={value}>
                        {label}
                      </option>
                    ),
                  )}
                </select>
              </label>
              <div>
                <button
                  type="button"
                  className="real-secondary-action"
                  onClick={() => setHandledElsewhereConfirmOpen(false)}
                  disabled={busy}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="real-handled-elsewhere-confirm-action"
                  onClick={() => {
                    setHandledElsewhereConfirmOpen(false);
                    onMarkHandledElsewhere?.(
                      handledElsewhereReasons[handledElsewhereReason],
                    );
                  }}
                  disabled={busy || !onMarkHandledElsewhere}
                >
                  确认已在别处处理
                </button>
              </div>
            </div>
          ) : null}
        </section>

        {businessSemantics.customerProfileApplicable ? (
          <section
            className="real-business-section"
            aria-labelledby="customer-profile-title"
          >
            <div className="real-business-section-heading">
              <div>
                <h2 id="customer-profile-title">客户画像</h2>
                <span
                  className={`real-grade-badge is-${customerDraft.customerGrade.toLowerCase()}`}
                >
                  {customerDraft.customerGrade === "PENDING"
                    ? "等级待确认"
                    : `${customerDraft.customerGrade} 类客户`}
                </span>
              </div>
              <div className="real-section-heading-actions">
                <button type="button" onClick={() => openPeek("CUSTOMER")}>
                  查看判断依据
                </button>
                <button
                  type="button"
                  className="real-secondary-action"
                  onClick={() => setCustomerEditorOpen(true)}
                >
                  编辑客户画像
                </button>
              </div>
            </div>
            <div className="real-customer-portrait-summary">
              {customer.domainVerification ? (
                <DomainVerificationCard
                  verification={customer.domainVerification}
                  decision={customer.domainVerificationDecision}
                  decidedAt={customer.domainVerificationReviewedAt}
                  workspaceDecision={
                    customer.domainVerificationWorkspaceDecision
                  }
                  releasedAt={
                    releasedDomainMarks?.[
                      customer.domainVerification.domain.trim().toLowerCase()
                    ]
                  }
                  busy={editorBusy}
                  onConfirm={() =>
                    void saveCustomerProfile(
                      {
                        ...customerDraft,
                        domainVerificationDecision: "CONFIRMED",
                      },
                      "人工确认邮箱域名归属及其证据",
                    )
                  }
                  onReject={() =>
                    void saveCustomerProfile(
                      {
                        ...customerDraft,
                        domainVerificationDecision: "REJECTED",
                      },
                      "人工确认邮箱域名为非法域名（仿冒/钓鱼风险）",
                    )
                  }
                  {...(onClearDomainRisk
                    ? {
                        onClearMark: () =>
                          onClearDomainRisk(
                            customer.domainVerification!.domain,
                          ),
                      }
                    : {})}
                />
              ) : null}
              <dl className="real-customer-profile-grid">
                <CustomerProfileField
                  label="客户等级"
                  value={
                    customerDraft.customerGrade === "PENDING"
                      ? "待人工确认"
                      : `${customerDraft.customerGrade} 类客户`
                  }
                  sources={customer.fieldSources?.customerGrade}
                  note={archiveGrade?.text ?? null}
                  noteTone={archiveGrade?.conflicts ? "attention" : undefined}
                />
                <CustomerProfileField
                  label="机构名称"
                  value={customerDraft.organizationName || "待确认"}
                  sources={customer.fieldSources?.organizationName}
                  placement="below-right"
                  note={
                    [
                      organizationIdentityStatusLabel(
                        customer.organizationIdentityStatus,
                      ),
                      archiveDomainNote(mboxHistory.state),
                    ]
                      .filter((value): value is string => Boolean(value))
                      .join(" · ") || null
                  }
                />
                <CustomerProfileField
                  label="国家/地区"
                  value={
                    formatCountryDisplay(customerDraft.country) || "待确认"
                  }
                  sources={customer.fieldSources?.country}
                />
                <CustomerProfileField
                  label="客户网站"
                  value={customerDraft.website || "待确认"}
                  href={customerDraft.website || undefined}
                  sources={customer.fieldSources?.website}
                  placement="below-right"
                />
                {/* Optional narrative fields only take space when there is something to read. */}
                {customerDraft.institutionIntroduction ? (
                  <CustomerProfileField
                    label="机构介绍"
                    value={customerDraft.institutionIntroduction}
                    sources={customer.fieldSources?.institutionIntroduction}
                    wide
                  />
                ) : null}
                {customerDraft.industryResearchCapability ? (
                  <CustomerProfileField
                    label="行业 / 研究能力"
                    value={customerDraft.industryResearchCapability}
                    sources={customer.fieldSources?.industryResearchCapability}
                    wide
                  />
                ) : null}
                {customerDraft.handlingRecommendation ? (
                  <CustomerProfileField
                    label="处理建议"
                    value={customerDraft.handlingRecommendation}
                    sources={customer.fieldSources?.handlingRecommendation}
                    wide
                  />
                ) : null}
              </dl>
              <CustomerHistoryBlock
                state={mboxHistory.state}
                inquiryCount={inquiryHistory.total}
                orderCount={customer.historicalOrderCount}
                onOpenHistory={() => openPeek("HISTORY")}
              />
            </div>
          </section>
        ) : (
          <section
            className="real-business-section real-communication-summary"
            aria-labelledby="communication-summary-title"
          >
            <div className="real-business-section-heading">
              <div>
                <h2 id="communication-summary-title">无需询单分发</h2>
              </div>
            </div>
            <strong>{businessSemantics.messageNatureLabel}</strong>
            <p>{businessSemantics.businessImpactSummary}</p>
            {isOrderReview ? (
              <OrderSalesReviewCard
                orderReview={orderReview ?? null}
                isOrderReview={isOrderReview}
                candidates={salesCandidates}
                selectedUserId={selectedSalesUserId}
                onChange={setSelectedSalesUserId}
                disabled={busy}
              />
            ) : null}
            <small>该邮件保留在邮件智能复核中，不进入客户询单分发队列。</small>
          </section>
        )}

        {teamConversionInquiryId ? (
          <TeamConversion
            inquiryId={teamConversionInquiryId}
            expectedVersion={detail.lifecycle?.processingVersion ?? 0}
            confirmedFields={confirmedFields}
            workspaceCode={workspaceCode}
            formId={teamConversionFormId}
            renderSubmitButton={false}
            onSubmitStateChange={setTeamConversionSubmitState}
            onSuccess={onNext}
          />
        ) : null}

        {!processed &&
        (businessSemantics.formalBusinessObject ||
          humanNewInquiryConfirmation) &&
        !isOrderReview &&
        detail.subject?.kind !== "FORMAL_INQUIRY" ? (
          <TriageActionBoard
            detail={detail}
            salesCandidates={salesCandidates}
            sourcingCandidates={sourcingCandidates}
            selectedSalesUserId={selectedSalesUserId}
            selectedSourcingUserId={selectedSourcingUserId}
            onSalesChange={setSelectedSalesUserId}
            onSourcingChange={setSelectedSourcingUserId}
            ccSalesArchive={ccSalesArchive}
            onCcSalesArchiveChange={setCcSalesArchive}
            busy={busy}
            onRunAgent={onRunAgent}
          />
        ) : null}

        {processed &&
        operationState !== "DISTRIBUTED_SUCCESS" &&
        businessSemantics.formalBusinessObject ? (
          <ProcessedBusinessResult
            detail={detail}
            onOpenCase={openCase}
            onRefresh={onRetry}
          />
        ) : null}
      </div>

      <RealStickyActionBar
        detail={detail}
        busy={busy}
        processed={processed}
        teamConversionAction={
          teamConversionInquiryId && teamConversionFormId
            ? {
                formId: teamConversionFormId,
                canSubmit:
                  teamConversionSubmitState.inquiryId ===
                    teamConversionInquiryId &&
                  teamConversionSubmitState.canSubmit,
                busy: teamConversionSubmitState.busy,
              }
            : undefined
        }
        onPrimaryAction={() => {
          if (primaryAction.type === "MERGE") {
            openMergeConfirm(
              mergeCandidates.map((candidate) => candidate.snapshotId),
              primaryAction.actionVersion,
            );
            return;
          }
          openForwardConfirm();
        }}
        onMarkNotInquiry={() => setNotInquiryConfirmOpen(true)}
        onMarkInvalidInquiry={() => setInvalidInquiryConfirmOpen(true)}
        onMarkHandledElsewhere={() => setHandledElsewhereConfirmOpen(true)}
        onDismissMerge={() =>
          onDismissMerge(
            mergeCandidates.map((candidate) => candidate.snapshotId),
            primaryAction.actionVersion,
          )
        }
        onOpenCase={openCase}
        onNext={onNext}
        onClose={onClose}
      />

      {peekOverlay}
      {modalOverlays}
    </main>
  );
}

function DomainVerificationCard({
  verification,
  decision,
  decidedAt,
  workspaceDecision,
  releasedAt,
  onConfirm,
  onReject,
  onClearMark,
  busy,
}: {
  verification: NonNullable<CustomerBusinessProfile["domainVerification"]>;
  decision?: CustomerBusinessProfile["domainVerificationDecision"];
  decidedAt?: CustomerBusinessProfile["domainVerificationReviewedAt"];
  /** Permanent Workspace verdict recorded in another inquiry of this Workspace. */
  workspaceDecision?: CustomerBusinessProfile["domainVerificationWorkspaceDecision"];
  /** Set after this operator released the Workspace-level permanent mark during this session. */
  releasedAt?: string;
  onConfirm: () => void;
  onReject: () => void;
  onClearMark?: () => void | Promise<void>;
  busy: boolean;
}) {
  const [clearConfirming, setClearConfirming] = useState(false);
  const [clearBusy, setClearBusy] = useState(false);
  const highRisk = verification.riskLevel === "HIGH";
  // A human verdict outranks the collected evidence: once someone decided, the card must not keep
  // asking for the review that already happened. The Workspace register counts as decided too, so a
  // domain confirmed in an earlier inquiry is not handed back as an undecided card.
  const workspaceVerdict =
    workspaceDecision?.decision === "CONFIRMED" ||
    workspaceDecision?.decision === "REJECTED"
      ? workspaceDecision.decision
      : null;
  const decidedFromWorkspace = !decision && workspaceVerdict !== null;
  const decidedState =
    decision === "REJECTED" || decision === "CONFIRMED"
      ? decision
      : workspaceVerdict;
  // Re-deciding stays a deliberate step: the buttons sit behind "更改判断" until the operator asks
  // for them. The toggle is keyed on the verdict it was opened for, so a saved change collapses the
  // panel again instead of leaving the two buttons open.
  const verdictKey = `${decision ?? ""}|${workspaceVerdict ?? ""}|${verification.domain}`;
  const [changingVerdictFor, setChangingVerdictFor] = useState<string | null>(
    null,
  );
  const changingVerdict = changingVerdictFor === verdictKey;
  const decidedAtValue = decision
    ? (decidedAt ?? null)
    : (workspaceDecision?.decidedAt ?? null);
  // "Cannot verify" is not "high risk": a suffix without public registration data still needs a
  // human, but it must not be presented as a confirmed danger.
  const needsReview =
    !decidedState &&
    !highRisk &&
    (verification.status === "REVIEW_REQUIRED" ||
      verification.requiresHumanReview);
  const badgeLabel = decidedState
    ? decidedState === "REJECTED"
      ? decidedFromWorkspace
        ? "本工作空间已确认 · 非法域名"
        : "已人工确认 · 非法域名"
      : decidedFromWorkspace
        ? "本工作空间已确认 · 属于该机构"
        : "已人工确认 · 属于该机构"
    : highRisk
      ? "高风险 · 必须人工复核"
      : needsReview
        ? "本次分析无法核实 · 待人工确认"
        : verification.status === "LOW_RISK"
          ? "暂未发现明显风险"
          : "证据不足";
  const badgeTone = decidedState
    ? decidedState === "REJECTED"
      ? " is-review"
      : " is-confirmed"
    : highRisk
      ? " is-review"
      : needsReview
        ? " is-pending"
        : "";
  const decidedLabel =
    decidedState === "REJECTED"
      ? decidedFromWorkspace
        ? "本工作空间已人工确认：非法域名（仿冒/钓鱼）"
        : "已人工确认：非法域名（仿冒/钓鱼）"
      : decidedState === "CONFIRMED"
        ? decidedFromWorkspace
          ? "本工作空间已人工确认：属于该机构"
          : "已人工确认：属于该机构"
        : null;
  const decidedTime = decidedAtValue ? formatDate(decidedAtValue) : null;
  const submitClearMark = async () => {
    if (!onClearMark) return;
    setClearBusy(true);
    try {
      await onClearMark();
      setClearConfirming(false);
    } catch {
      // The workspace renders the failure; leave the confirmation open so it can be retried.
    } finally {
      setClearBusy(false);
    }
  };
  return (
    <div
      className={`domain-verification-card${highRisk ? " is-review" : ""}${
        needsReview ? " is-pending" : ""
      }`}
    >
      <div className="domain-verification-card__heading">
        <strong>邮箱域名真实性验证</strong>
        <span className={`domain-verification-card__badge${badgeTone}`}>
          {badgeLabel}
        </span>
      </div>
      {decidedLabel ? (
        <p
          className={`domain-verification-card__decision is-${
            decidedState === "REJECTED" ? "rejected" : "confirmed"
          }`}
        >
          {decidedLabel}
          {decidedTime ? ` · 确认时间 ${decidedTime}` : null}
          {decidedFromWorkspace
            ? " · 结论来自本工作空间的其它询单，本次分析已直接复用，无需再次确认"
            : null}
          {decidedState === "REJECTED"
            ? " · 该域名已进入永久风险库：同域来信不再重复查询，并直接拦截销售 / 查货分发"
            : null}
        </p>
      ) : null}
      {releasedAt ? (
        <p className="domain-verification-card__release">
          已解除本工作空间的永久标记（{verification.domain}）· 解除时间{" "}
          {formatDate(releasedAt)}
          ：下次分析会重新核查该域名，可能再次回到待确认状态；本询单的人工确认记录保留。
        </p>
      ) : null}
      <dl className="domain-verification-card__facts">
        <div>
          <dt>发件人域名</dt>
          <dd>{verification.domain}</dd>
        </div>
        <div>
          <dt>官方域名候选</dt>
          <dd>{verification.officialDomainCandidate || "未找到可验证候选"}</dd>
        </div>
        <div>
          <dt>页面声明机构</dt>
          <dd>{verification.claimedOrganizationName || "未识别"}</dd>
        </div>
      </dl>
      {/*
        Once a human decided, the "we could not verify this" reasons and the "not upgraded to a
        formal organization until a human confirms" hint are superseded: rendering them next to
        the verdict is what made a settled domain look like it still awaited review. The collected
        evidence stays in the analysis artifact, the register snapshot and the audit trail.
      */}
      {decidedState ? null : (
        <>
          {verification.reasons.length ? (
            <ul className="domain-verification-card__reasons">
              {verification.reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : (
            <p className="domain-verification-card__empty">
              当前没有足够的域名风险证据。
            </p>
          )}
          <small>
            域名存在网站不等于域名属于该机构；人工确认前不会升级为正式机构。
          </small>
        </>
      )}
      {/*
        A decided domain — here or in another inquiry of this Workspace — must not look like it is
        asking again: the verdict stands, and re-deciding is an explicit, deliberate step.
      */}
      <div className="domain-verification-card__actions">
        {decidedState && !changingVerdict ? (
          <button
            type="button"
            className="real-secondary-action"
            onClick={() => setChangingVerdictFor(verdictKey)}
            disabled={busy || clearBusy}
          >
            更改判断
          </button>
        ) : (
          <>
            <button
              type="button"
              className="real-confirm-action"
              onClick={onConfirm}
              disabled={busy || clearBusy}
            >
              {busy ? "保存中…" : "确认域名属于该机构"}
            </button>
            <button
              type="button"
              className="real-danger-action"
              onClick={onReject}
              disabled={busy || clearBusy}
            >
              {busy ? "保存中…" : "确认域名为非法域名"}
            </button>
            {decidedState ? (
              <button
                type="button"
                className="real-secondary-action"
                onClick={() => setChangingVerdictFor(null)}
                disabled={busy || clearBusy}
              >
                取消
              </button>
            ) : null}
          </>
        )}
        {decidedState && onClearMark && !clearConfirming ? (
          <button
            type="button"
            className="real-secondary-action"
            onClick={() => setClearConfirming(true)}
            disabled={busy || clearBusy}
            title="仅工作空间管理员可执行；解除后下次分析会重新核查该域名"
          >
            解除域名风险标记
          </button>
        ) : null}
      </div>
      {decidedState && onClearMark && clearConfirming ? (
        <div className="domain-verification-card__confirm">
          <p>
            解除后，本工作空间内 <strong>{verification.domain}</strong>{" "}
            的永久标记会被释放，下次分析会重新核查该域名，可能再次回到待确认状态；本次解除会写入审计。
            本询单的人工确认记录不受影响。
          </p>
          <div className="domain-verification-card__actions">
            <button
              type="button"
              className="real-danger-action"
              onClick={() => void submitClearMark()}
              disabled={busy || clearBusy}
            >
              {clearBusy ? "解除中…" : "确认解除永久标记"}
            </button>
            <button
              type="button"
              className="real-secondary-action"
              onClick={() => setClearConfirming(false)}
              disabled={busy || clearBusy}
            >
              取消
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MergeConfirmationItem({
  inquiryId,
  product,
  cas,
  quantity,
  submittedAt,
  current = false,
  onOpenOriginalMail,
}: {
  inquiryId: string | null;
  product: string | null | undefined;
  cas: string | null | undefined;
  quantity: string | null | undefined;
  submittedAt: string | null | undefined;
  current?: boolean;
  onOpenOriginalMail?: (inquiryId: string) => void;
}) {
  return (
    <article>
      <div>
        <strong>{product ?? "产品待确认"}</strong>
        {current ? <span>当前询单</span> : null}
      </div>
      <p>
        {cas ? `CAS ${cas}` : "CAS 待确认"} · {quantity ?? "数量待确认"}
      </p>
      <small>{submittedAt ? formatDate(submittedAt) : "提交时间待确认"}</small>
      {inquiryId && onOpenOriginalMail ? (
        <button
          type="button"
          className="real-secondary-action real-merge-original-mail-action"
          aria-label={`查看原邮件：${current ? "当前询单" : (product ?? "候选询单")}`}
          onClick={() => onOpenOriginalMail(inquiryId)}
        >
          查看原邮件
        </button>
      ) : null}
    </article>
  );
}

function ModalFrame({
  title,
  children,
  onClose,
  ariaLabel = title,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  ariaLabel?: string;
}) {
  return (
    <div
      className="triage-modal-layer"
      data-overlay-layer="action-confirmation"
    >
      <button
        type="button"
        className="triage-modal-backdrop"
        aria-label="关闭弹窗"
        onClick={onClose}
      />
      <section
        className="triage-modal"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        <header>
          <div>
            <span>分诊台信息</span>
            <h2>{title}</h2>
          </div>
          <button
            type="button"
            className="triage-modal-close"
            onClick={onClose}
            aria-label="关闭"
          >
            ×
          </button>
        </header>
        <div className="triage-modal-content">{children}</div>
      </section>
    </div>
  );
}

function MailOriginalModal({
  evidence,
  originalMail,
  inquiryId,
  loading,
  error,
  onClose,
  onRetry,
}: {
  evidence: TriageBusinessWorkbenchDetail["mailEvidence"];
  originalMail: OriginalMailMessage | null;
  inquiryId: string | null;
  loading: boolean;
  error: string;
  onClose: () => void;
  onRetry: () => void;
}) {
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<
    string | null
  >(null);
  const [downloadError, setDownloadError] = useState("");
  const body = originalMail?.textBody ?? evidence?.safeText ?? "";
  const thread =
    originalMail?.thread.messages ?? evidence?.thread.messages ?? [];
  const downloadAttachment = async (attachmentId: string) => {
    if (!inquiryId) return;
    setDownloadError("");
    setDownloadingAttachmentId(attachmentId);
    try {
      const result = await downloadOriginalMailAttachment(
        inquiryId,
        attachmentId,
      );
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.filename;
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError(
        "附件暂时无法下载。请确认附件未被隔离、大小在限制内，并重试。",
      );
    } finally {
      setDownloadingAttachmentId(null);
    }
  };
  return (
    <ModalFrame title="邮件原文" onClose={onClose}>
      {loading ? (
        <p className="triage-modal-status">正在读取邮件原文…</p>
      ) : null}
      {error ? (
        <div className="triage-modal-error" role="alert">
          <p>{error}</p>
          <button type="button" onClick={onRetry}>
            重新加载
          </button>
        </div>
      ) : null}
      {!loading && !error ? (
        <>
          <dl className="triage-modal-meta">
            <div>
              <dt>主题</dt>
              <dd>
                {originalMail?.subject ?? evidence?.subject ?? "主题待确认"}
              </dd>
            </div>
            <div>
              <dt>发件人</dt>
              <dd>
                {mailAddressLabel(originalMail?.sender ?? evidence?.sender)}
              </dd>
            </div>
            <div>
              <dt>收件人</dt>
              <dd>
                {recipientLabel(
                  originalMail?.recipients ?? evidence?.recipients ?? [],
                )}
              </dd>
            </div>
            <div>
              <dt>时间</dt>
              <dd>
                {originalMail?.receivedAt
                  ? formatDate(originalMail.receivedAt)
                  : evidence?.receivedAt
                    ? formatDate(evidence.receivedAt)
                    : "时间待确认"}
              </dd>
            </div>
          </dl>
          <OriginalMailBody
            textBody={body}
            htmlBody={originalMail?.htmlBody ?? null}
          />
          {(originalMail?.attachments ?? evidence?.attachments ?? []).length ? (
            <section className="triage-original-attachments">
              <header>
                <strong>附件</strong>
                <span>只读查看</span>
              </header>
              {(originalMail?.attachments ?? evidence?.attachments ?? []).map(
                (attachment) => (
                  <div key={attachment.id}>
                    <div>
                      <strong>{attachment.filename}</strong>
                      <span>
                        {attachment.contentType} ·{" "}
                        {formatBytes(attachment.size)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="real-secondary-action"
                      disabled={
                        !inquiryId ||
                        ["QUARANTINED", "REJECTED"].includes(
                          attachment.securityStatus,
                        ) ||
                        downloadingAttachmentId === attachment.id
                      }
                      onClick={() => void downloadAttachment(attachment.id)}
                    >
                      {downloadingAttachmentId === attachment.id
                        ? "正在下载…"
                        : ["QUARANTINED", "REJECTED"].includes(
                              attachment.securityStatus,
                            )
                          ? "附件已隔离"
                          : "下载附件"}
                    </button>
                  </div>
                ),
              )}
              {downloadError ? (
                <p className="triage-modal-error" role="alert">
                  {downloadError}
                </p>
              ) : null}
            </section>
          ) : null}
          {thread.length > 1 ? (
            <section className="triage-original-thread">
              <header>
                <strong>邮件线程</strong>
                <span>{thread.length} 封邮件</span>
              </header>
              {thread.map((message) => (
                <details key={message.id}>
                  <summary>
                    <strong>
                      {message.direction === "INBOUND"
                        ? "收到的邮件"
                        : "发出的邮件"}
                    </strong>
                    <span>
                      {"sender" in message && message.sender
                        ? mailAddressLabel(message.sender)
                        : message.subject}
                    </span>
                    <small>{formatDate(message.occurredAt)}</small>
                  </summary>
                  <article>
                    <strong>{message.subject}</strong>
                    <MailPlainText
                      text={
                        ("textBody" in message
                          ? message.textBody
                          : message.excerpt) || null
                      }
                      emptyLabel="正文为空"
                    />
                    {message.attachments?.length ? (
                      <div className="triage-original-thread-attachments">
                        {message.attachments.map((attachment) => (
                          <div key={attachment.id}>
                            <span>
                              {attachment.filename} ·{" "}
                              {formatBytes(attachment.size)}
                            </span>
                            <button
                              type="button"
                              className="real-secondary-action"
                              disabled={
                                !inquiryId ||
                                ["QUARANTINED", "REJECTED"].includes(
                                  attachment.securityStatus,
                                ) ||
                                downloadingAttachmentId === attachment.id
                              }
                              onClick={() =>
                                void downloadAttachment(attachment.id)
                              }
                            >
                              {downloadingAttachmentId === attachment.id
                                ? "正在下载…"
                                : ["QUARANTINED", "REJECTED"].includes(
                                      attachment.securityStatus,
                                    )
                                  ? "附件已隔离"
                                  : "下载附件"}
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </article>
                </details>
              ))}
            </section>
          ) : null}
        </>
      ) : null}
    </ModalFrame>
  );
}

function ProductInformationEditor({
  initial,
  busy,
  error,
  onClose,
  onSave,
}: {
  initial: ConfirmedInquiryFields;
  busy: boolean;
  error: string;
  onClose: () => void;
  onSave: (fields: ConfirmedInquiryFields, reason?: string) => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [reason, setReason] = useState("");
  // The workbench adopts the edited values as soon as saving starts, so `initial` can change while
  // this editor is open (a rejected save does that). Compare against the values the editor opened
  // with, so a failed save still leaves the edits, the reason and an enabled save button in place.
  const [baseline] = useState(initial);
  const changed = JSON.stringify(draft) !== JSON.stringify(baseline);
  const fields = [
    ["productName", "产品名称", 240],
    ["cas", "CAS", 80],
    ["catalogNumber", "Cat No", 120],
    ["quantity", "数量", 120],
    ["specification", "规格", 120],
    ["intendedUse", "用途", 400],
    ["destinationCountry", "收货国家", 80],
  ] as const;
  return (
    <ModalFrame title="补充产品信息" onClose={onClose}>
      <p className="triage-modal-intro">
        系统会优先使用邮件原文和已验证的分析结果；这里只补充当前无法确认的字段。
      </p>
      <div className="triage-form-grid">
        {fields.map(([key, label, maxLength]) => (
          <label key={key}>
            <span>{label}</span>
            <input
              value={draft[key]}
              placeholder="未提取"
              maxLength={maxLength}
              onChange={(event) =>
                setDraft({ ...draft, [key]: event.target.value })
              }
            />
          </label>
        ))}
      </div>
      {changed ? (
        <label className="triage-form-reason">
          <span>补充或修改原因（可选）</span>
          <textarea
            value={reason}
            placeholder="可选：例如原文写明 30 / 60 / 90 / 100 tablets，人工补充数量范围"
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
      ) : null}
      {error ? (
        <p className="triage-modal-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="triage-modal-actions">
        <button
          type="button"
          className="real-secondary-action"
          onClick={onClose}
          disabled={busy}
        >
          取消
        </button>
        <button
          type="button"
          className="triage-primary-action"
          onClick={() => onSave(draft, reason || undefined)}
          disabled={busy || !changed}
        >
          {busy ? "保存中…" : "保存补充"}
        </button>
      </div>
    </ModalFrame>
  );
}

function CustomerProfileEditor({
  initial,
  busy,
  error,
  onClose,
  onSave,
}: {
  initial: CustomerProfileDraft;
  busy: boolean;
  error: string;
  onClose: () => void;
  onSave: (profile: CustomerProfileDraft, reason?: string) => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [reason, setReason] = useState("");
  // Same baseline rule as the product editor: a rejected save must not hide the reason field or
  // disable the save button the operator needs for a retry.
  const [baseline] = useState(initial);
  const changed = JSON.stringify(draft) !== JSON.stringify(baseline);
  return (
    <ModalFrame title="编辑客户画像" onClose={onClose}>
      <p className="triage-modal-intro">
        邮箱、域名和原始 DeepSeek
        判断保持只读；客户等级会保存到当前工作空间内已关联的客户机构，
        同一机构的新询单会自动继承。其他字段仍是当前分诊工作台使用的人工画像。
      </p>
      <div className="triage-form-grid">
        <label>
          <span>客户等级</span>
          <select
            value={draft.customerGrade}
            onChange={(event) =>
              setDraft({
                ...draft,
                customerGrade: event.target
                  .value as CustomerProfileDraft["customerGrade"],
              })
            }
          >
            <option value="PENDING">待确认</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
            <option value="D">D</option>
            <option value="E">E</option>
          </select>
        </label>
        <label>
          <span>国家/地区</span>
          <input
            value={draft.country ?? ""}
            placeholder="待确认"
            maxLength={80}
            onChange={(event) =>
              setDraft({ ...draft, country: event.target.value || null })
            }
          />
        </label>
        <label>
          <span>客户网站</span>
          <input
            value={draft.website ?? ""}
            placeholder="待确认"
            maxLength={500}
            onChange={(event) =>
              setDraft({ ...draft, website: event.target.value || null })
            }
          />
        </label>
        <label className="triage-form-wide">
          <span>机构名称</span>
          <input
            value={draft.organizationName ?? ""}
            placeholder="待确认"
            maxLength={240}
            onChange={(event) =>
              setDraft({
                ...draft,
                organizationName: event.target.value || null,
              })
            }
          />
        </label>
        <label className="triage-form-wide">
          <span>机构介绍</span>
          <textarea
            value={draft.institutionIntroduction}
            placeholder="例如：Ghent University 是比利时公立研究型大学"
            maxLength={500}
            onChange={(event) =>
              setDraft({
                ...draft,
                institutionIntroduction: event.target.value,
              })
            }
          />
        </label>
        <label className="triage-form-wide">
          <span>行业 / 研究能力</span>
          <textarea
            value={draft.industryResearchCapability}
            placeholder="填写与本次业务判断有关的行业、研发或采购能力"
            maxLength={500}
            onChange={(event) =>
              setDraft({
                ...draft,
                industryResearchCapability: event.target.value,
              })
            }
          />
        </label>
        <label className="triage-form-wide">
          <span>处理建议</span>
          <textarea
            value={draft.handlingRecommendation}
            placeholder="例如：按正式客户询单处理，销售结合查货结果跟进"
            maxLength={500}
            onChange={(event) =>
              setDraft({ ...draft, handlingRecommendation: event.target.value })
            }
          />
        </label>
      </div>
      {changed ? (
        <label className="triage-form-reason">
          <span>修改原因（可选）</span>
          <textarea
            value={reason}
            placeholder="可选：请说明为什么需要修正客户等级、国家或机构判断"
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
      ) : null}
      {error ? (
        <p className="triage-modal-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="triage-modal-actions">
        <button
          type="button"
          className="real-secondary-action"
          onClick={onClose}
          disabled={busy}
        >
          取消
        </button>
        <button
          type="button"
          className="triage-primary-action"
          onClick={() => onSave(draft, reason || undefined)}
          disabled={busy || !changed}
        >
          {busy ? "保存中…" : "保存客户画像"}
        </button>
      </div>
    </ModalFrame>
  );
}

function mailAddressLabel(
  value: { name: string | null; email: string | null } | undefined,
): string {
  if (!value) return "发件人待确认";
  return (
    [value.name, value.email].filter(Boolean).join(" · ") || "发件人待确认"
  );
}

function recipientLabel(
  recipients: ReadonlyArray<{ name: string | null; email: string | null }>,
): string {
  return (
    recipients
      .map((recipient) =>
        [recipient.name, recipient.email].filter(Boolean).join(" · "),
      )
      .filter(Boolean)
      .join("、") || "收件人待确认"
  );
}

function ProcessedBusinessResult({
  detail,
  onOpenCase,
  onRefresh,
}: {
  detail: TriageBusinessWorkbenchDetail;
  onOpenCase: (caseId: string | null | undefined) => void;
  onRefresh: () => void;
}) {
  const result = processingResultOf(detail);
  const tasks = result.tasks ?? [];
  const assignments = result.assignments ?? [];
  const assignmentTarget = result.assignmentTarget;
  const teamDistribution = assignmentTarget?.distributionMode === "TEAM";
  const assignmentRecipients: Array<{
    key: string;
    label: string;
    value: string;
  }> = [];
  const teamLabel = assignmentTarget?.teamName?.trim()
    ? assignmentTarget.teamName
    : assignmentTarget?.teamId
      ? `团队 ${assignmentTarget.teamId.slice(0, 8)}`
      : null;
  const ownerLabel = assignmentTarget?.ownerDisplayName?.trim()
    ? assignmentTarget.ownerDisplayName
    : assignmentTarget?.ownerUserId
      ? `成员 ${assignmentTarget.ownerUserId.slice(0, 8)}`
      : null;
  if (teamLabel && (teamDistribution || assignments.length === 0)) {
    assignmentRecipients.push({
      key: `case-team-${assignmentTarget?.teamId ?? teamLabel}`,
      label: "处理团队",
      value: `${teamLabel}${assignmentTarget?.memberCount != null ? `（分发时 ${assignmentTarget.memberCount} 人）` : ""}`,
    });
  }
  if (ownerLabel && (teamDistribution || assignments.length === 0)) {
    assignmentRecipients.push({
      key: `case-owner-${assignmentTarget?.ownerUserId ?? ownerLabel}`,
      label: "案件负责人",
      value: ownerLabel,
    });
  }
  for (const assignment of teamDistribution ? [] : assignments) {
    const assignedTeam = assignment.teamName?.trim()
      ? assignment.teamName
      : assignment.teamId
        ? `团队 ${assignment.teamId.slice(0, 8)}`
        : null;
    const assignedPerson = assignment.primaryAssigneeName?.trim()
      ? assignment.primaryAssigneeName
      : assignment.primaryAssigneeId
        ? `成员 ${assignment.primaryAssigneeId.slice(0, 8)}`
        : null;
    const identities = [
      assignedTeam ? `团队：${assignedTeam}` : null,
      assignedPerson ? `人员：${assignedPerson}` : null,
    ].filter((value): value is string => Boolean(value));
    if (identities.length) {
      assignmentRecipients.push({
        key: assignment.id,
        label:
          assignment.objectType === "INQUIRY_CASE" ? "销售分配" : "查货分配",
        value: identities.join(" · "),
      });
    }
  }
  const invalidInquiryClosed = result.finalAction === "INVALID_INQUIRY_CLOSED";
  const handledElsewhereClosed =
    result.finalAction === "HANDLED_ELSEWHERE_CLOSED";
  const closedWithoutBusinessEffects =
    invalidInquiryClosed || handledElsewhereClosed;
  const closureHeadline = invalidInquiryClosed
    ? "无效询单，已关闭"
    : handledElsewhereClosed
      ? "已在 Gmail/他处处理，已关闭"
      : "当前询单已完成内部业务处理";
  return (
    <section
      className="real-business-section real-processed-result"
      aria-label="处理结果"
    >
      <div className="real-business-section-heading">
        <div>
          <h2>
            {closedWithoutBusinessEffects ? "询单处置结果" : "分发与处理结果"}
          </h2>
          <span className="real-grade-badge">已完成</span>
        </div>
      </div>
      <div className="real-operation-result is-merged" role="status">
        <strong>{closureHeadline}</strong>
        {closedWithoutBusinessEffects ? (
          <>
            <p>{detail.businessSemantics?.businessImpactSummary}</p>
            <p>
              客户询单性质与证据均已保留；案件、销售/查货分配、任务及外发邮件均为零。
              {result.processedAt
                ? ` · ${new Date(result.processedAt).toLocaleString("zh-CN", {
                    hour12: false,
                  })}`
                : ""}
            </p>
          </>
        ) : (
          <>
            <p>
              案件：
              {result.caseNumber ?? result.caseId?.slice(0, 8) ?? "未创建案件"}
              {result.processedAt
                ? ` · ${new Date(result.processedAt).toLocaleString("zh-CN", {
                    hour12: false,
                  })}`
                : ""}
            </p>
            <p>
              {teamDistribution
                ? "分配模式：团队共同处理"
                : `销售分配：${result.salesAssignmentId ? "已建立" : "未建立"} · 查货分配：${result.sourcingAssignmentId ? "已建立" : "按策略未建立"}`}{" "}
              · 产品任务：{tasks.length || result.taskIds.length} 个
            </p>
          </>
        )}
        {assignmentRecipients.length ? (
          <dl className="real-processing-assignees" aria-label="分配对象">
            {assignmentRecipients.map((recipient) => (
              <div key={recipient.key}>
                <dt>{recipient.label}</dt>
                <dd>{recipient.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {assignments.length ? (
          <ul>
            {assignments.map((assignment) => (
              <li key={assignment.id}>
                {assignment.objectType === "INQUIRY_CASE"
                  ? "销售案件"
                  : "产品任务"}{" "}
                · {assignment.status}
              </li>
            ))}
          </ul>
        ) : null}
        {tasks.length ? (
          <ul>
            {tasks.map((task) => (
              <li key={task.id}>
                {task.title} · {task.status}
              </li>
            ))}
          </ul>
        ) : null}
        <InternalNotificationReceipts
          notifications={result.notifications ?? []}
          teamMembers={teamDistribution ? assignmentTarget?.members : undefined}
          syncEmail={teamDistribution ? assignmentTarget?.syncEmail : undefined}
          teamMemberSnapshotAvailable={
            teamDistribution
              ? assignmentTarget?.memberSnapshotAvailable
              : undefined
          }
          onChanged={onRefresh}
        />
      </div>
    </section>
  );
}

function RealStickyActionBar({
  detail,
  busy,
  processed,
  teamConversionAction,
  onPrimaryAction,
  onMarkNotInquiry,
  onMarkInvalidInquiry,
  onMarkHandledElsewhere,
  onDismissMerge,
  onOpenCase,
  onNext,
  onClose,
}: {
  detail: TriageBusinessWorkbenchDetail;
  busy: boolean;
  processed: boolean;
  teamConversionAction?: {
    formId: string;
    canSubmit: boolean;
    busy: boolean;
  };
  onPrimaryAction: () => void;
  onMarkNotInquiry: () => void;
  onMarkInvalidInquiry: () => void;
  onMarkHandledElsewhere: () => void;
  onDismissMerge: () => void;
  onOpenCase: (caseId: string | null | undefined) => void;
  onNext: () => void;
  onClose: () => void;
}) {
  const action = detail.primaryActionCapability;
  const actionLabel = primaryActionBusinessLabel(detail);
  const mergeCandidates = detail.duplicateProfile.mergeCandidates ?? [];
  const result = processingResultOf(detail);
  const semantics = businessSemanticsOf(detail);
  const dedicatedReviewOnly =
    action.type === "REVIEW_DECISION" &&
    !allowsHumanNewInquiryConfirmation(detail) &&
    detail.businessSemantics?.recordKind !== "ORDER_REVIEW" &&
    detail.emailIntelligence?.orderReview?.isOrderCandidate !== true;
  if (semantics.recordKind === "COMMUNICATION_ONLY") {
    return (
      <nav className="real-sticky-actions" aria-label="通信记录操作">
        <button type="button" className="triage-primary-action" disabled>
          {semantics.queueBucketLabel}
        </button>
        <button
          type="button"
          className="real-secondary-action"
          onClick={onNext}
        >
          处理下一条
        </button>
        <button
          type="button"
          className="real-secondary-action"
          onClick={onClose}
        >
          关闭
        </button>
      </nav>
    );
  }
  if (processed || action.type === "VIEW_RESULT") {
    return (
      <nav className="real-sticky-actions" aria-label="已处理操作">
        {result.caseId ? (
          <button type="button" onClick={() => onOpenCase(result.caseId)}>
            查看案件
          </button>
        ) : null}
        <button
          type="button"
          className="real-secondary-action"
          onClick={onNext}
        >
          处理下一条
        </button>
        <button
          type="button"
          className="real-secondary-action"
          onClick={onClose}
        >
          关闭
        </button>
      </nav>
    );
  }
  if (action.type === "MERGE") {
    return (
      <nav className="real-sticky-actions" aria-label="合并复核辅助操作">
        <span className="real-sticky-actions-hint">
          请在询单与需求区域选择合并或分别处理
        </span>
        <button
          type="button"
          className="real-secondary-action"
          onClick={onClose}
        >
          关闭
        </button>
      </nav>
    );
  }
  return (
    <nav className="real-sticky-actions" aria-label="当前询单主操作">
      {detail.subject?.kind === "FORMAL_INQUIRY" &&
      detail.subject.dataOrigin === "REAL_GMAIL" ? (
        <>
          <button
            type="button"
            className="real-secondary-action"
            onClick={onMarkNotInquiry}
            disabled={busy}
          >
            不是客户询单
          </button>
          <button
            type="button"
            className="real-secondary-action real-invalid-inquiry-action"
            onClick={onMarkInvalidInquiry}
            disabled={busy}
          >
            无效询单
          </button>
          <button
            type="button"
            className="real-secondary-action real-handled-elsewhere-action"
            onClick={onMarkHandledElsewhere}
            disabled={busy}
          >
            已在 Gmail/他处处理
          </button>
        </>
      ) : null}
      {teamConversionAction ? (
        <button
          type="submit"
          form={teamConversionAction.formId}
          className="triage-primary-action"
          disabled={
            busy || teamConversionAction.busy || !teamConversionAction.canSubmit
          }
        >
          {teamConversionAction.busy ? "正在分发…" : "确认客户询单并分发"}
        </button>
      ) : (
        <button
          type="button"
          className="triage-primary-action"
          onClick={onPrimaryAction}
          disabled={
            busy ||
            !action.allowed ||
            action.type === "NONE" ||
            dedicatedReviewOnly
          }
          title={
            dedicatedReviewOnly
              ? "该邮件性质需要专用业务复核，不能按客户新询价分发"
              : (action.disabledReason ?? undefined)
          }
        >
          {busy ? "正在执行…" : actionLabel}
        </button>
      )}
      {dedicatedReviewOnly ? (
        <span className="real-sticky-actions-hint">
          该记录需要专用业务复核，不能按客户新询价分发
        </span>
      ) : null}
    </nav>
  );
}

function OrderSalesReviewCard({
  orderReview,
  isOrderReview,
  candidates,
  selectedUserId,
  onChange,
  disabled,
}: {
  orderReview: NonNullable<
    TriageBusinessWorkbenchDetail["emailIntelligence"]
  >["orderReview"];
  isOrderReview: boolean;
  candidates: DistributionCandidate[];
  selectedUserId: string;
  onChange: (userId: string) => void;
  disabled: boolean;
}) {
  if (!isOrderReview) return null;
  return (
    <div className="real-order-review-card" aria-label="销售订单复核">
      <div>
        <strong>采购订单候选</strong>
        <span>
          {orderReview?.orderNumber
            ? `订单号：${orderReview.orderNumber}`
            : "订单号待确认"}
        </span>
      </div>
      <p>
        {orderReview?.reasons?.[0] ??
          "邮件已识别为采购订单候选，请由销售核对订单内容与附件后继续处理。"}
      </p>
      <label>
        销售负责人
        <select
          value={selectedUserId}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
        >
          {candidates.map((candidate) => (
            <option key={candidate.userId} value={candidate.userId}>
              {candidate.alias}
            </option>
          ))}
        </select>
      </label>
      <small>
        提交后仅进入销售订单复核，不创建查货任务，不向客户或供应商外发。
      </small>
    </div>
  );
}

function TriageActionBoard({
  detail,
  salesCandidates,
  sourcingCandidates,
  selectedSalesUserId,
  selectedSourcingUserId,
  onSalesChange,
  onSourcingChange,
  ccSalesArchive,
  onCcSalesArchiveChange,
  busy,
  onRunAgent,
}: {
  detail: TriageBusinessWorkbenchDetail;
  salesCandidates: DistributionCandidate[];
  sourcingCandidates: DistributionCandidate[];
  selectedSalesUserId: string;
  selectedSourcingUserId: string;
  onSalesChange: (userId: string) => void;
  onSourcingChange: (userId: string) => void;
  ccSalesArchive: boolean;
  onCcSalesArchiveChange: (value: boolean) => void;
  busy: boolean;
  onRunAgent?: () => void;
}) {
  const primaryAction = detail.primaryActionCapability;
  const semantics = businessSemanticsOf(detail);
  const decision = triageDecisionProfileOf(detail);
  const orderReview = detail.emailIntelligence?.orderReview;
  const isOrderReview =
    semantics.recordKind === "ORDER_REVIEW" ||
    orderReview?.isOrderCandidate === true;
  const showStaffSelectors =
    ["SINGLE_FORWARD", "GROUP_FORWARD", "PARTIAL_FORWARD"].includes(
      primaryAction.type,
    ) ||
    allowsHumanNewInquiryConfirmation(detail) ||
    isOrderReview;
  const realGmailFormal =
    detail.subject?.kind === "FORMAL_INQUIRY" &&
    detail.subject.dataOrigin === "REAL_GMAIL";
  return (
    <section
      className="real-action-board real-distribution-settings"
      aria-label="分发设置"
    >
      <div className="real-action-board-heading">
        <div>
          <h2>分发设置</h2>
          <p>确认后，销售和查货会同时收到案件与任务通知。</p>
        </div>
      </div>
      {allowsHumanNewInquiryConfirmation(detail) ? (
        <p className="real-human-confirmation-note">
          当前邮件曾被判断为一般业务沟通。请依据上方邮件正文、客户画像和产品风险确认它是否属于客户询单。
        </p>
      ) : null}
      {showStaffSelectors ? (
        <div className="real-action-board-selectors">
          <label>
            销售
            <select
              value={selectedSalesUserId}
              onChange={(event) => onSalesChange(event.target.value)}
              disabled={busy}
            >
              {salesCandidates.map((candidate) => (
                <option key={candidate.userId} value={candidate.userId}>
                  {candidate.alias}
                </option>
              ))}
            </select>
          </label>
          {!isOrderReview ? (
            <label>
              查货
              <select
                value={selectedSourcingUserId}
                onChange={(event) => onSourcingChange(event.target.value)}
                disabled={busy}
              >
                {sourcingCandidates.map((candidate) => (
                  <option key={candidate.userId} value={candidate.userId}>
                    {candidate.alias}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      ) : null}
      {showStaffSelectors && realGmailFormal ? (
        <label className="real-suppress-sender-option">
          <input
            type="checkbox"
            checked={ccSalesArchive}
            onChange={(event) => onCcSalesArchiveChange(event.target.checked)}
            disabled={busy}
          />
          <span>
            抄送
            sales@benchchem.com（运营归档）：过渡期测试可取消勾选，本次分发及该案件后续通知都不再抄送
          </span>
        </label>
      ) : null}
      {onRunAgent &&
      semantics.recordKind !== "COMMUNICATION_ONLY" &&
      ["NOT_STARTED", "FAILED", "STALE"].includes(decision.analysisStatus) ? (
        <button
          type="button"
          className="real-secondary-action real-run-agent-action"
          onClick={onRunAgent}
          disabled={busy}
        >
          重新形成辅助判断
        </button>
      ) : null}
    </section>
  );
}

function defaultSourcingStrategy(
  detail: TriageBusinessWorkbenchDetail,
): HumanGmailSourcingStrategy {
  return detail.distributionDecision.recentReusableHistory.status === "REUSABLE"
    ? "REVIEW_REUSABLE_HISTORY"
    : "RECHECK_SUPPLY";
}

function primaryActionSource(detail: TriageBusinessWorkbenchDetail): string {
  const distribution = detail.distributionDecision;
  const decision = triageDecisionProfileOf(detail);
  if (detail.primaryActionCapability.type === "MERGE")
    return "同一客户 12 小时窗口与询单关系规则";
  if (detail.primaryActionCapability.type === "NONE")
    return "确定性邮件性质业务门禁";
  if (distribution.action === "SALES_DIRECT_QUOTE")
    return "历史查货与有效报价复用规则";
  if (distribution.action === "CONTINUE_EXISTING_CASE")
    return "CRM 现有案件关联事实";
  if (distribution.action === "HANDLE_DUPLICATE") return "确定性重复识别证据";
  if (distribution.action === "REQUEST_INFORMATION")
    return "询单字段完整性规则";
  if (distribution.action === "REJECT_INQUIRY") return "已确认的业务阻断事实";
  if (decision.source === "TRIAGE_AGENT_V1")
    return "DeepSeek / TRIAGE_AGENT_V1 + 确定性安全校验";
  if (decision.source === "SYNTHETIC_FIXTURE")
    return "UAT 固定场景 + 确定性业务规则";
  if (decision.source === "LEGACY_FALLBACK") return "历史规则兜底";
  return "确定性业务规则";
}

function ExistingCaseEvidence({
  linkedCase,
  historicalOnly = false,
  onOpenCase,
}: {
  linkedCase: NonNullable<DistributionDecision["linkedCase"]>;
  historicalOnly?: boolean;
  onOpenCase: (caseId: string | null | undefined) => void;
}) {
  const archivedHistoricalLink =
    historicalOnly && linkedCase.status === "ARCHIVED";
  return (
    <section
      className="real-existing-case-evidence"
      aria-label={historicalOnly ? "历史案件关联审计" : "现有案件关联证据"}
    >
      <header>
        <div>
          <strong>
            {archivedHistoricalLink
              ? "历史归档案件关联"
              : historicalOnly
                ? "历史案件关联审计"
                : "本询单已属于现有案件"}
          </strong>
          <p>
            {historicalOnly
              ? "该关联仅用于追溯历史，不会恢复或重新分发案件。"
              : "继续处理会沿用当前案件，不会重复创建案件；销售和查货将收到这次新增需求。"}
          </p>
        </div>
        <button type="button" onClick={() => onOpenCase(linkedCase.id)}>
          打开案件 {linkedCase.reference}
        </button>
      </header>
      <DescriptionRows
        rows={[
          ["案件状态", caseStatusLabel(linkedCase.status)],
          ["创建时间", formatDate(linkedCase.createdAt)],
          ["最近活动", formatDate(linkedCase.updatedAt)],
          ["主要负责人", linkedCase.ownerName ?? "待分配"],
          ["客户", linkedCase.customerName ?? "客户主体待确认"],
          ["相关产品", linkedCase.relatedProduct ?? "产品待确认"],
          [
            historicalOnly ? "历史保留说明" : "关联原因",
            historicalOnly
              ? "仅保留原始关联用于审计；不会创建、恢复或分发任何业务对象。"
              : linkedCase.associationReason,
          ],
        ]}
      />
    </section>
  );
}

function DistributionSuccessResult({
  result,
  detail,
  onOpenCase,
  onNext,
  onClose,
}: {
  result:
    | RealShadowDistributionResult
    | RealShadowGroupDistributionResult
    | FormalInquiryDistributionResult;
  detail: TriageBusinessWorkbenchDetail;
  onOpenCase: (caseId: string | null | undefined) => void;
  onNext: () => void;
  onClose: () => void;
}) {
  const grouped = "products" in result;
  const formal = "inquiryId" in result && !("snapshotId" in result);
  const products = grouped
    ? result.products
    : [
        {
          snapshotId: formal ? result.inquiryId : result.snapshotId,
          reference: result.inquiryReference,
          product: detail.inquiryDetailProfile.productName,
          cas: detail.inquiryDetailProfile.cas,
          quantity: detail.inquiryDetailProfile.quantity,
          taskIds: result.taskIds,
          status: "DISTRIBUTED" as const,
        },
      ];
  const sales = grouped
    ? candidateLabel(result.salesAssignee)
    : candidateLabel(detail.distributionDecision.salesCandidate);
  const sourcing = grouped
    ? candidateLabel(result.sourcingAssignee)
    : candidateLabel(detail.distributionDecision.sourcingCandidate);
  const notifications = formal ? result.notifications : [];
  return (
    <div className="real-operation-result is-distributed" role="status">
      <span>转发完成</span>
      <strong>案件与负责人已建立</strong>
      <dl>
        <div>
          <dt>案件</dt>
          <dd>{result.caseNumber ?? result.caseId?.slice(0, 8) ?? "未创建"}</dd>
        </div>
        <div>
          <dt>销售</dt>
          <dd>{sales} · 已分配</dd>
        </div>
        <div>
          <dt>查货</dt>
          <dd>
            {sourcing} · {result.sourcingAssignmentId ? "已分配" : "按策略跳过"}
          </dd>
        </div>
      </dl>
      <div className="real-result-products">
        <strong>产品任务</strong>
        {products.map((product, index) => (
          <div key={`${product.snapshotId ?? "product"}:${index}`}>
            <span>{product.product ?? "产品待确认"}</span>
            <small>
              {product.cas ? `CAS ${product.cas} · ` : ""}
              {product.quantity ?? "数量待确认"} · {product.taskIds.length}{" "}
              个任务
            </small>
            <em>已转发</em>
          </div>
        ))}
        {grouped
          ? result.blockedProducts.map((product) => (
              <div key={product.snapshotId} className="is-blocked">
                <span>{product.product ?? "产品待确认"}</span>
                <small>{product.reason}</small>
                <em>保留待复核</em>
              </div>
            ))
          : null}
      </div>
      {formal ? (
        <InternalNotificationReceipts notifications={notifications} />
      ) : null}
      <div className="real-result-actions">
        <button type="button" onClick={() => onOpenCase(result.caseId)}>
          打开案件生命周期工作台
        </button>
        <button
          type="button"
          className="real-secondary-action"
          onClick={onNext}
        >
          处理下一条
        </button>
        <button
          type="button"
          className="real-secondary-action"
          onClick={onClose}
        >
          关闭
        </button>
      </div>
    </div>
  );
}

function InternalNotificationReceipts({
  notifications,
  teamMembers,
  syncEmail,
  teamMemberSnapshotAvailable,
  onChanged,
}: {
  notifications: readonly InternalRoleNotificationResult[];
  teamMembers?: ReadonlyArray<{
    userId: string;
    displayName: string;
    emailAvailable: boolean;
    email?: string | null;
    emailMasked: string | null;
  }>;
  syncEmail?: boolean | null;
  teamMemberSnapshotAvailable?: boolean;
  onChanged?: () => void;
}) {
  const [busyDeliveryId, setBusyDeliveryId] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");
  const teamNotifications: InternalRoleNotificationResult[] = (
    teamMembers ?? []
  ).map((member) => {
    const notification = notifications.find(
      (candidate) => candidate.recipientUserId === member.userId,
    );
    if (notification)
      return {
        ...notification,
        recipientEmail: member.email ?? notification.recipientEmail,
      };
    return {
      role: "SALES",
      recipientKind: "TEAM_MEMBER",
      recipientUserId: member.userId,
      recipientDisplayName: member.displayName,
      recipientEmail: member.email ?? undefined,
      recipientMasked: member.emailMasked ?? undefined,
      state:
        syncEmail === false
          ? "SYNC_DISABLED"
          : member.emailAvailable
            ? "QUEUED"
            : "SKIPPED_NO_EMAIL",
      channels: [],
    } as InternalRoleNotificationResult;
  });
  const displayedNotifications = teamMembers
    ? [
        ...teamNotifications,
        ...notifications.filter(
          (notification) => notification.recipientKind !== "TEAM_MEMBER",
        ),
      ]
    : notifications;
  const act = async (
    action: "RETRY" | "CONFIRM",
    delivery: Extract<
      InternalRoleNotificationResult["channels"][number],
      { channel: string }
    >,
  ) => {
    if (!delivery.deliveryId || !delivery.rowVersion) return;
    const confirmed = window.confirm(
      action === "RETRY"
        ? "确认重新发送这条内部通知？系统会重新生成一条可审计的发送任务。"
        : "确认已经在对应测试邮箱中看到这封邮件？",
    );
    if (!confirmed) return;
    setBusyDeliveryId(delivery.deliveryId);
    setActionError("");
    try {
      if (action === "RETRY") {
        await retryRoleNotification(
          delivery.deliveryId,
          delivery.rowVersion,
          "分诊员从处理结果申请重新发送内部角色通知",
        );
      } else {
        await confirmRoleNotificationReceived(
          delivery.deliveryId,
          delivery.rowVersion,
          "分诊员已在允许的测试邮箱中确认收到邮件",
        );
      }
      onChanged?.();
    } catch {
      setActionError("状态已变化或操作失败，请刷新当前询单后重试。");
    } finally {
      setBusyDeliveryId(null);
    }
  };
  return (
    <section
      className="real-result-notifications"
      aria-label="内部邮件通知结果"
    >
      <header>
        <div>
          <strong>内部邮件投递</strong>
          <small>
            {teamMembers
              ? "按分发时团队成员快照记录，后续成员变更不影响历史"
              : "按角色记录投递状态；销售由查货通知抄送"}
          </small>
        </div>
        <span>客户与供应商外发 0</span>
      </header>
      <div className="real-notification-lanes">
        {displayedNotifications.map((notification) => {
          const typedChannels = notification.channels.filter(
            (
              channel,
            ): channel is Extract<
              InternalRoleNotificationResult["channels"][number],
              { channel: string }
            > => typeof channel !== "string",
          );
          const retry = typedChannels.find(
            (channel) => channel.state === "FAILED",
          );
          const confirm = typedChannels.find(
            (channel) =>
              channel.channel === "EXTERNAL_SMTP" &&
              (channel.state === "SES_ACCEPTED" ||
                channel.state === "SES_DELIVERED"),
          );
          return (
            <article
              className={`real-notification-lane ${notificationStateClass(notification.state)}`}
              key={`${notification.role}:${notification.recipientUserId ?? notification.outboxEventId ?? "queued"}`}
            >
              <header>
                <div>
                  <span>
                    {notification.recipientKind === "TEAM_MEMBER"
                      ? (notification.recipientDisplayName ?? "团队成员")
                      : notification.role === "SALES"
                        ? "销售"
                        : "查货"}
                  </span>
                  <strong>{notificationStateLabel(notification.state)}</strong>
                </div>
                <small>
                  {notification.recipientEmail ??
                    notification.recipientMasked ??
                    (notification.state === "SKIPPED_NO_EMAIL"
                      ? "未配置联系邮箱"
                      : "收件地址由团队策略确定")}
                </small>
              </header>
              <ol>
                {(notification.state === "SKIPPED_NO_EMAIL" ||
                notification.state === "SYNC_DISABLED"
                  ? []
                  : notification.channels.length
                    ? notification.channels
                    : (["LOCAL_MAILPIT", "EXTERNAL_SMTP"] as const)
                ).map((channel) => {
                  const name =
                    typeof channel === "string" ? channel : channel.channel;
                  const state =
                    typeof channel === "string" ? "QUEUED" : channel.state;
                  const time =
                    typeof channel === "string"
                      ? null
                      : (channel.manuallyConfirmedAt ??
                        channel.acceptedAt ??
                        channel.latestAttemptAt);
                  return (
                    <li key={name} className={notificationStateClass(state)}>
                      <span>
                        {name === "LOCAL_MAILPIT"
                          ? "本地留证"
                          : name === "GMAIL_ORIGINAL_COPY"
                            ? "原件副本"
                            : "测试邮箱"}
                      </span>
                      <strong>{channelStateLabel(name, state)}</strong>
                      {time ? (
                        <small>{formatNotificationTime(time)}</small>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
              {onChanged && (retry || confirm) ? (
                <div className="real-notification-actions">
                  {retry ? (
                    <button
                      type="button"
                      disabled={busyDeliveryId === retry.deliveryId}
                      onClick={() => void act("RETRY", retry)}
                    >
                      {busyDeliveryId === retry.deliveryId
                        ? "正在排队…"
                        : "重新发送"}
                    </button>
                  ) : null}
                  {confirm ? (
                    <button
                      type="button"
                      className="real-secondary-action"
                      disabled={busyDeliveryId === confirm.deliveryId}
                      onClick={() => void act("CONFIRM", confirm)}
                    >
                      {busyDeliveryId === confirm.deliveryId
                        ? "正在确认…"
                        : "确认邮箱已收到"}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </article>
          );
        })}
        {!displayedNotifications.length ? (
          <p>
            {teamMembers && teamMemberSnapshotAvailable === false
              ? "该历史分发发生在成员快照启用前，无法准确还原当时的收件成员和投递状态。"
              : "案件已建立，内部通知状态正在刷新。"}
          </p>
        ) : null}
      </div>
      {actionError ? <p role="alert">{actionError}</p> : null}
      <footer>
        内部邮件只投递给分发时记录的角色或团队成员；客户与供应商外发：0。
      </footer>
    </section>
  );
}

function notificationStateLabel(
  state: InternalRoleNotificationResult["state"],
): string {
  const labels: Record<InternalRoleNotificationResult["state"], string> = {
    QUEUED: "等待发送",
    DELIVERED: "投递通道已完成",
    LOCAL_MAILPIT_ACCEPTED: "本地记录完成",
    SES_ACCEPTED: "SES 已接受，等待确认收件",
    GMAIL_ACCEPTED: "原件副本已由 info@evitachem.com 发出",
    ACCEPTANCE_UNKNOWN: "发送结果待核实，未自动重发",
    SES_DELIVERED: "收件服务器已接收",
    DELIVERY_DELAYED: "收件服务器延迟接收",
    BOUNCED: "邮件已退回",
    COMPLAINT: "收件人报告为垃圾邮件",
    REJECTED: "SES 拒绝发送",
    FAILED: "发送失败",
    MANUALLY_CONFIRMED: "已确认测试邮箱收到",
    SKIPPED_NO_EMAIL: "未配置联系邮箱，未投递",
    SYNC_DISABLED: "分发时未启用同步邮件",
  };
  return labels[state];
}

function channelStateLabel(
  channel: "LOCAL_MAILPIT" | "EXTERNAL_SMTP" | "GMAIL_ORIGINAL_COPY",
  state: InternalRoleNotificationResult["state"],
): string {
  if (channel === "LOCAL_MAILPIT")
    return state === "LOCAL_MAILPIT_ACCEPTED" ||
      state === "DELIVERED" ||
      state === "MANUALLY_CONFIRMED"
      ? "已保留发送证据"
      : state === "FAILED"
        ? "本地留证失败"
        : "等待本地留证";
  if (channel === "GMAIL_ORIGINAL_COPY")
    return state === "GMAIL_ACCEPTED"
      ? "Gmail 已接受，销售可引用原文"
      : state === "FAILED"
        ? "原件副本发送失败，需人工重发"
        : "等待发送原件副本";
  return notificationStateLabel(state);
}

function notificationStateClass(
  state: InternalRoleNotificationResult["state"],
): string {
  if (
    state === "LOCAL_MAILPIT_ACCEPTED" ||
    state === "SES_ACCEPTED" ||
    state === "GMAIL_ACCEPTED" ||
    state === "SES_DELIVERED" ||
    state === "MANUALLY_CONFIRMED" ||
    state === "DELIVERED"
  )
    return "is-success";
  if (
    state === "FAILED" ||
    state === "BOUNCED" ||
    state === "COMPLAINT" ||
    state === "REJECTED"
  )
    return "is-failed";
  if (state === "ACCEPTANCE_UNKNOWN" || state === "DELIVERY_DELAYED")
    return "is-attention";
  if (state === "SKIPPED_NO_EMAIL" || state === "SYNC_DISABLED")
    return "is-attention";
  return "is-pending";
}

function formatNotificationTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "时间待刷新"
    : date.toLocaleString("zh-CN", { hour12: false });
}

function CustomerMessage({
  inquiry,
  label = "客户留言",
}: {
  inquiry: InquiryDetailProfile;
  label?: string;
}) {
  const message = inquiry.projectDetails?.trim();
  if (!message) return null;
  return (
    <div className="real-customer-message">
      <strong>{label}</strong>
      <details>
        <summary>查看原文</summary>
        <p>{message}</p>
      </details>
    </div>
  );
}

function MailEvidencePanel({
  evidence,
  judgment,
  onOpenEvidence,
  onOpenOriginal,
}: {
  evidence: NonNullable<TriageBusinessWorkbenchDetail["mailEvidence"]>;
  judgment?: MailJudgment | null;
  onOpenEvidence: () => void;
  onOpenOriginal: () => void;
}) {
  const sender = [evidence.sender.name, evidence.sender.email]
    .filter(Boolean)
    .join(" · ");
  const customerIdentity = evidence.customerIdentity;
  const customerContact = [customerIdentity?.name, customerIdentity?.email]
    .filter(Boolean)
    .join(" · ");
  return (
    <article className="real-mail-evidence" aria-label="客户邮件证据">
      <header>
        <div>
          <span>邮件主题</span>
          <strong>{evidence.subject || "主题未填写"}</strong>
        </div>
      </header>
      <div className="real-mail-judgment" aria-label="邮件判断">
        <div className="real-mail-judgment-heading">
          <div>
            <span>邮件判断</span>
            <strong>{judgment?.label ?? "邮件性质待确认"}</strong>
          </div>
          <div className="real-mail-analysis-status" aria-label="邮件分析状态">
            <InlineHint
              label="邮件分析状态"
              placement="below-right"
              tone={
                judgment?.analysisStatus === "STALE" ||
                judgment?.analysisStatus === "FAILED"
                  ? "attention"
                  : "default"
              }
              text={mailJudgmentHintText(judgment)}
            />
          </div>
        </div>
        <div className="real-mail-judgment-actions">
          <button
            type="button"
            className="real-evidence-link"
            onClick={onOpenEvidence}
          >
            查看判断依据
          </button>
          <button
            type="button"
            className="real-evidence-link"
            onClick={onOpenOriginal}
          >
            查看邮件原文
          </button>
        </div>
      </div>
      <dl>
        <div>
          <dt>{customerIdentity ? "客户联系人" : "发件人"}</dt>
          <dd>{customerContact || sender || "联系人待确认"}</dd>
        </div>
        {customerIdentity?.organizationName ? (
          <div>
            <dt>客户机构</dt>
            <dd>{customerIdentity.organizationName}</dd>
          </div>
        ) : null}
        {customerIdentity?.advisory ? (
          <div>
            <dt>DeepSeek 建议</dt>
            <dd>
              {deepseekCustomerAdviceText(customerIdentity.advisory)}
              {customerIdentity.advisory.freshness === "STALE" ? (
                <span className="real-mail-advisory-note">
                  分析结果已过期，等待最新一轮邮件分析
                </span>
              ) : null}
            </dd>
          </div>
        ) : null}
      </dl>
      {evidence.attachments.length ? (
        <div className="real-mail-source-hint">
          <span>{evidence.attachments.length} 个附件可在邮件原文中查看</span>
        </div>
      ) : null}
    </article>
  );
}

function MailJudgmentPeek({ judgment }: { judgment?: MailJudgment | null }) {
  if (!judgment) {
    return <p className="real-peek-hint">尚未形成可展示的邮件判断。</p>;
  }
  return (
    <div className="real-peek-stack">
      <article className="real-evidence-card real-evidence-summary">
        <strong>{judgment.label}</strong>
        <p>{judgment.summary}</p>
        <div className="real-intelligence-status-line">
          <span>{mailAnalysisStatusLabel(judgment)}</span>
          <span>{mailJudgmentSourceLabel(judgment)}</span>
        </div>
      </article>
      <section className="real-source-section">
        <h3>判断依据</h3>
        {judgment.evidence.length ? (
          judgment.evidence.map((entry, index) => (
            <article
              className="real-source-item"
              key={`${entry.source}-${index}`}
            >
              <strong>{entry.statement}</strong>
              <span>来源：{mailEvidenceSourceLabel(entry.source)}</span>
            </article>
          ))
        ) : (
          <p className="real-peek-hint">
            当前没有可引用的判断依据，请查看邮件原文。
          </p>
        )}
      </section>
      {judgment.businessReasons?.length ? (
        <section className="real-source-section">
          <h3>业务判断理由</h3>
          <ul className="real-evidence-list">
            {judgment.businessReasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function mailJudgmentSourceLabel(judgment?: MailJudgment | null): string {
  if (!judgment) return "尚无判断来源";
  if (judgment.source === "HYBRID") return "DeepSeek + 原文校验";
  if (judgment.source === "DEEPSEEK") return "DeepSeek 建议";
  if (judgment.source === "DETERMINISTIC") return "原文规则校验";
  if (judgment.source === "HUMAN") return "人工确认";
  return "来源待确认";
}

function mailAnalysisStatusLabel(judgment?: MailJudgment | null): string {
  if (!judgment) return "尚未分析";
  if (judgment.analysisStatus === "READY" && judgment.artifactAvailable)
    return judgment.completeThread
      ? `DeepSeek 已完整解读 · ${judgment.analyzedMessageCount} 封邮件`
      : `DeepSeek 已解读现有 ${judgment.analyzedMessageCount} 封邮件，线程仍不完整`;
  if (judgment.analysisStatus === "RUNNING") return "分析中";
  if (judgment.analysisStatus === "FAILED") return "分析失败，可人工判断";
  if (judgment.analysisStatus === "STALE") return "结果已过期";
  return "尚未分析";
}

/** Everything the 邮件判断 card used to print below the conclusion, kept for the hover hint. */
function mailJudgmentHintText(judgment?: MailJudgment | null): string {
  return [
    `分析状态：${mailAnalysisStatusLabel(judgment)}`,
    `判断来源：${mailJudgmentSourceLabel(judgment)}`,
    `判断摘要：${
      judgment?.summary ??
      "当前没有可展示的结构化判断，请先查看邮件原文并人工确认。"
    }`,
  ].join("\n");
}

function mailEvidenceSourceLabel(source: string): string {
  if (source === "DEEPSEEK") return "DeepSeek 建议";
  if (source === "EMAIL_DOMAIN") return "发件人域名";
  if (source === "THREAD") return "邮件线程";
  if (source === "RULE") return "确定性规则";
  if (source === "HUMAN") return "人工确认";
  return "邮件正文";
}

const deepseekOrganizationTypeLabels: Record<string, string> = {
  UNIVERSITY: "大学",
  RESEARCH_INSTITUTION: "科研机构",
  GOVERNMENT: "政府机构",
  NONPROFIT: "非营利组织",
  BUSINESS: "企业",
  CORPORATE: "企业",
  ENTERPRISE: "企业",
  OEM: "OEM 厂商",
  FACTORY: "工厂",
  DISTRIBUTOR: "代理商 / 经销商",
  PURCHASING_AGENT: "采购代理",
  TRADING_COMPANY: "贸易公司",
  AGENT: "代理",
  INDIVIDUAL: "个人",
};

/**
 * DeepSeek's own customer suggestion, printed beside the recorded identity. It is advisory only:
 * the mailbox and contact shown next to it always come from the Inquiry record.
 */
function deepseekCustomerAdviceText(
  advisory: NonNullable<
    NonNullable<
      TriageBusinessWorkbenchDetail["mailEvidence"]
    >["customerIdentity"]
  >["advisory"],
): string {
  const organizationType = advisory?.organizationType
    ? (deepseekOrganizationTypeLabels[
        advisory.organizationType.toUpperCase()
      ] ?? advisory.organizationType)
    : null;
  const parts = [
    advisory?.organizationName,
    organizationType,
    advisory?.country,
    advisory?.customerGrade ? `${advisory.customerGrade} 类客户` : null,
  ].filter((value): value is string => Boolean(value));
  return parts.length ? parts.join(" · ") : "暂无可用建议";
}

function ProductEvidenceCard({
  inquiry,
  confirmedFields,
  risk,
  history,
  showCustomerMessage,
  onOpenRisk,
  onOpenEditor,
  fieldStates,
  humanOverride,
  archiveState,
  onReloadArchive,
}: {
  inquiry: InquiryDetailProfile;
  confirmedFields: ConfirmedInquiryFields;
  risk: ProductRiskProfile;
  history?: ProductCommercialHistorySummary;
  showCustomerMessage: boolean;
  onOpenRisk: () => void;
  onOpenEditor: () => void;
  fieldStates?: TriageBusinessWorkbenchDetail["products"][number]["fieldStates"];
  humanOverride?: TriageBusinessWorkbenchDetail["products"][number]["humanOverride"];
  archiveState: MboxHistoryLoadState;
  onReloadArchive: () => void;
}) {
  const commercialFacts = [
    history?.quotes.latest
      ? ["历史报价", historyPrice(history.quotes.latest)]
      : null,
    history?.salesPrices?.latest
      ? ["历史销售价", historyPrice(history.salesPrices.latest)]
      : null,
  ].filter((entry): entry is [string, string] => Boolean(entry));
  const secondaryFields = (
    [
      ["catalogNumber", "Cat No"],
      ["quantity", "数量"],
      ["specification", "规格"],
      ["intendedUse", "用途"],
      ["destinationCountry", "收货国家"],
    ] as const
  ).map(([key, label], index) => ({
    label,
    value: confirmedFields[key],
    // 右列字段的提示向右伸展会顶到卡片边缘，改为向左伸展。
    note: productFieldHint(
      fieldStates?.[key],
      index % 2 === 0 ? "below-right" : "above",
    ),
  }));
  return (
    <article className="real-product-evidence-card" aria-label="产品询单信息">
      <ProductInquiryCardContent
        productName={confirmedFields.productName}
        productNameNote={productFieldHint(fieldStates?.productName)}
        cas={confirmedFields.cas}
        casNote={productFieldHint(fieldStates?.cas)}
        risk={risk}
        secondaryFields={secondaryFields}
        onOpenRisk={onOpenRisk}
        onOpenEditor={onOpenEditor}
        editNote={
          humanOverride
            ? {
                text: `已人工补充：${humanOverride.fields.join("、")}`,
                tone: "human" as const,
              }
            : {
                text: "原文没有提供或识别不完整的字段，可由分诊员补充。",
                tone: "default" as const,
              }
        }
      />
      {showCustomerMessage && inquiry.projectDetails ? (
        <div className="real-product-customer-note">
          <strong>客户需求说明</strong>
          <p>{inquiry.projectDetails}</p>
        </div>
      ) : null}
      {commercialFacts.length ? (
        <DescriptionRows rows={commercialFacts} />
      ) : null}
      <MboxHistorySection state={archiveState} onReload={onReloadArchive} />
    </article>
  );
}

type OperatorRiskView = NonNullable<ProductRiskProfile["operatorView"]>;
type OperatorRiskDimension = OperatorRiskView["dimensions"][number];

/**
 * 资料不足时后端与前端回退都会给出这一句结论。它不是风险判断，卡片上不显示这一行；
 * 具体分项仍可在“查看风险判断依据”里读到。
 */
const UNRESOLVED_RISK_CONCLUSION = "资料不足";

function ProductInquiryCardContent({
  productName,
  productNameNote,
  cas,
  casNote,
  risk,
  secondaryFields,
  onOpenRisk,
  onOpenEditor,
  editNote,
  statusLabel,
}: {
  productName: string | null | undefined;
  productNameNote?: FieldHint;
  cas: string | null | undefined;
  casNote?: FieldHint;
  risk: ProductRiskProfile;
  secondaryFields: Array<{
    label: string;
    value: string | null | undefined;
    note?: FieldHint;
  }>;
  onOpenRisk: () => void;
  onOpenEditor?: () => void;
  editNote?: FieldHint;
  statusLabel?: string;
}) {
  const operatorView = productRiskOperatorView(risk);
  const productFields: Array<{
    label: string;
    value: string | null | undefined;
    note?: FieldHint;
    /** 固定占位：即使没有内容也保留这一行。 */
    pinned?: boolean;
  }> = [
    { label: "产品名称", value: productName, note: productNameNote },
    { label: "CAS", value: cas, note: casNote, pinned: true },
    ...secondaryFields,
  ];
  // 空字段不再占一行：运营只读有内容的字段。唯一的例外是带 attention 提示的空字段，
  // 例如“关键信息可能位于内嵌图片，尚未解析”，那本身就是需要人工核实的信息。
  const visibleFields = productFields.filter(
    (field) =>
      field.pinned ||
      Boolean(field.value?.trim()) ||
      field.note?.tone === "attention",
  );
  const showRiskConclusion =
    operatorView.handlingConclusion !== UNRESOLVED_RISK_CONCLUSION;
  return (
    <>
      <header className="product-information-header">
        <div>
          <span className="product-information-heading-row">
            <strong>产品信息</strong>
            {statusLabel ? <em>{statusLabel}</em> : null}
            {editNote ? (
              <InlineHint
                label="字段补充说明"
                placement="below"
                tone={editNote.tone}
                text={editNote.text}
              />
            ) : null}
          </span>
        </div>
        {onOpenEditor ? (
          <button
            type="button"
            className="real-secondary-action"
            onClick={onOpenEditor}
          >
            补充产品信息
          </button>
        ) : null}
      </header>
      <dl className="product-information-grid" aria-label="产品基本信息">
        {visibleFields.map((field) => (
          <div
            className={field.label === "产品名称" ? "is-wide" : undefined}
            key={field.label}
          >
            <dt>{field.label}</dt>
            <dd>
              {field.value?.trim() || "无"}
              {field.note ? (
                <InlineHint
                  label={`${field.label}字段来源`}
                  placement={field.note.placement}
                  tone={field.note.tone}
                  text={field.note.text}
                />
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
      <section
        className="real-product-risk-summary"
        aria-label="产品与风险判断"
      >
        <header>
          <strong>产品与风险判断</strong>
          <button
            type="button"
            className="real-evidence-link"
            onClick={onOpenRisk}
          >
            查看风险判断依据
          </button>
        </header>
        {showRiskConclusion ? (
          <dl>
            <div className="product-risk-summary-line">
              <dt>风险结论</dt>
              <dd>{operatorView.handlingConclusion}</dd>
            </div>
          </dl>
        ) : null}
      </section>
    </>
  );
}

export function productRiskOperatorView(
  risk: ProductRiskProfile,
): OperatorRiskView {
  const base = risk.operatorView ?? fallbackProductRiskOperatorView(risk);
  const destination = normalizeCountryName(
    risk.routeContext?.destinationCountry ?? risk.destinationLabel,
  );
  if (destination !== "中国" && destination !== "美国") return base;
  const controlKey = destination === "中国" ? "CHINA_CONTROL" : "US_CONTROL";
  const control = base.dimensions.find(({ key }) => key === controlKey);
  const destinationControl = base.dimensions.find(
    ({ key }) => key === "DESTINATION_CONTROL",
  );
  const merged = mergeOperatorDimensions(
    control,
    destinationControl,
    destination === "中国" ? "中国管制" : "美国管制",
  );
  return {
    ...base,
    dimensions: base.dimensions
      .filter(({ key }) => key !== "DESTINATION_CONTROL")
      .map((dimension) => (dimension.key === controlKey ? merged : dimension)),
  };
}

function fallbackProductRiskOperatorView(
  risk: ProductRiskProfile,
): OperatorRiskView {
  const dimensions: OperatorRiskDimension[] = [
    fallbackControlDimension(
      "CHINA_CONTROL",
      "中国管制",
      risk.chinaControl,
      risk,
    ),
    fallbackControlDimension("US_CONTROL", "美国管制", risk.usControl, risk),
    fallbackControlDimension(
      "DESTINATION_CONTROL",
      "目标国管制",
      risk.destinationControl,
      risk,
    ),
    fallbackTransportDimension(risk),
    fallbackGasDimension(risk),
  ];
  const hasConfirmed = dimensions.some(
    ({ state }) => state === "CONFIRMED_RISK",
  );
  const hasSignal = dimensions.some(({ state }) => state === "RISK_SIGNAL");
  const unresolved = dimensions.some(({ state }) => state === "UNRESOLVED");
  return {
    handlingConclusion: hasConfirmed
      ? "发现明确风险，人工审核后决定是否继续分发"
      : hasSignal
        ? "可以分发；查货人员重点核实已发现的管控或运输风险线索"
        : unresolved
          ? UNRESOLVED_RISK_CONCLUSION
          : "可以正常分发",
    dimensions,
  };
}

function fallbackControlDimension(
  key: Extract<
    OperatorRiskDimension["key"],
    "CHINA_CONTROL" | "US_CONTROL" | "DESTINATION_CONTROL"
  >,
  label: string,
  status: ProductRiskProfile["chinaControl"],
  risk: ProductRiskProfile,
): OperatorRiskDimension {
  const finding = risk.findings.find(({ dimension }) => dimension === key);
  const category = productRiskCategory(finding, `${label}要求`);
  if (status === "RESTRICTED")
    return operatorDimension(
      key,
      label,
      "CONFIRMED_RISK",
      `发现该化合物属于${category}，人工审核后决定是否继续分发`,
      finding,
    );
  if (finding && ["REVIEW", "BLOCKING"].includes(finding.severity))
    return operatorDimension(
      key,
      label,
      "RISK_SIGNAL",
      `发现可能涉及${category}管控的线索，建议查货重点核对此项管控风险`,
      finding,
    );
  if (status === "PENDING")
    return operatorDimension(
      key,
      label,
      "UNRESOLVED",
      UNRESOLVED_RISK_CONCLUSION,
    );
  return operatorDimension(key, label, "NO_RISK_FOUND", "未发现管制风险");
}

function fallbackTransportDimension(
  risk: ProductRiskProfile,
): OperatorRiskDimension {
  const finding =
    risk.findings.find(({ dimension }) => dimension === "DANGEROUS_GOODS") ??
    risk.findings.find(({ dimension }) => dimension === "TRANSPORT");
  const category = productRiskCategory(finding, "危险品运输管控");
  if (risk.dangerousGoods === "YES" || risk.transportRisk === "UNAVAILABLE")
    return operatorDimension(
      "TRANSPORT",
      "运输风险",
      "CONFIRMED_RISK",
      `发现该化合物属于${category}，人工审核后决定是否继续分发`,
      finding,
    );
  if (
    ["SPECIAL", "DIFFICULT"].includes(risk.transportRisk) ||
    (finding && ["REVIEW", "BLOCKING"].includes(finding.severity))
  )
    return operatorDimension(
      "TRANSPORT",
      "运输风险",
      "RISK_SIGNAL",
      `发现可能涉及${category}的线索，建议查货重点核对此项运输风险`,
      finding,
    );
  if (risk.transportRisk === "PENDING" || risk.dangerousGoods === "PENDING")
    return operatorDimension(
      "TRANSPORT",
      "运输风险",
      "UNRESOLVED",
      UNRESOLVED_RISK_CONCLUSION,
    );
  return operatorDimension(
    "TRANSPORT",
    "运输风险",
    "NO_RISK_FOUND",
    "未发现特殊运输风险",
  );
}

function fallbackGasDimension(risk: ProductRiskProfile): OperatorRiskDimension {
  const finding = risk.findings.find(({ dimension }) => dimension === "GAS");
  if (risk.isGas === "YES" || risk.gasBusinessState === "CONFIRMED_GAS")
    return operatorDimension(
      "GAS",
      "是否气体",
      "CONFIRMED_RISK",
      "发现该化合物属于气体产品运输管控，人工审核后决定是否继续分发",
      finding,
    );
  if (risk.gasBusinessState === "GAS_CONFIRMATION_REQUIRED" && finding)
    return operatorDimension(
      "GAS",
      "是否气体",
      "RISK_SIGNAL",
      "发现可能以气体形式供货的线索，建议查货重点核实实际供货形态",
      finding,
    );
  if (risk.isGas === "PENDING" || risk.gasBusinessState === "UNKNOWN")
    return operatorDimension(
      "GAS",
      "是否气体",
      "UNRESOLVED",
      UNRESOLVED_RISK_CONCLUSION,
    );
  return operatorDimension(
    "GAS",
    "是否气体",
    "NO_RISK_FOUND",
    "未发现气体风险",
  );
}

function operatorDimension(
  key: OperatorRiskDimension["key"],
  label: string,
  state: OperatorRiskDimension["state"],
  conclusion: string,
  finding?: ProductRiskProfile["findings"][number],
): OperatorRiskDimension {
  return {
    key,
    label,
    state,
    conclusion,
    riskCategoryCode: null,
    riskCategoryLabel: null,
    evidenceSummary:
      state === "RISK_SIGNAL" || state === "CONFIRMED_RISK"
        ? (finding?.reason ?? null)
        : null,
    sourceLabel:
      state === "RISK_SIGNAL" || state === "CONFIRMED_RISK"
        ? (finding?.sourceLabel ?? null)
        : null,
    sourceUrl:
      state === "RISK_SIGNAL" || state === "CONFIRMED_RISK"
        ? (finding?.sourceUrl ?? null)
        : null,
    requiresHumanReview: state === "CONFIRMED_RISK",
  };
}

function mergeOperatorDimensions(
  primary: OperatorRiskDimension | undefined,
  destination: OperatorRiskDimension | undefined,
  label: string,
): OperatorRiskDimension {
  const fallback = operatorDimension(
    label === "中国管制" ? "CHINA_CONTROL" : "US_CONTROL",
    label,
    "NO_RISK_FOUND",
    "未发现管制风险",
  );
  const selected = [primary, destination]
    .filter((entry): entry is OperatorRiskDimension => Boolean(entry))
    .sort(
      (left, right) =>
        operatorRiskPriority(right.state) - operatorRiskPriority(left.state),
    )[0];
  return selected ? { ...selected, key: fallback.key, label } : fallback;
}

function operatorRiskPriority(state: OperatorRiskDimension["state"]): number {
  return state === "CONFIRMED_RISK" ? 2 : state === "RISK_SIGNAL" ? 1 : 0;
}

function operatorRiskStateClass(state: OperatorRiskDimension["state"]): string {
  return state === "CONFIRMED_RISK"
    ? "is-confirmed"
    : state === "RISK_SIGNAL"
      ? "is-signal"
      : "is-no-risk";
}

function productRiskCategory(
  finding: ProductRiskProfile["findings"][number] | undefined,
  fallback: string,
): string {
  const text = finding
    ? `${finding.title} ${finding.reason} ${finding.impact} ${finding.recommendation}`
    : "";
  const categories: Array<[RegExp, string]> = [
    [/易制毒|precursor/i, "易制毒化学品"],
    [/精神药品|精神类|psychotropic/i, "精神类物质"],
    [/麻醉药品|narcotic/i, "麻醉药品"],
    [/受控物质|controlled substance|DEA/i, "受控物质"],
    [/两用物项|dual[ -]?use|ECCN/i, "两用物项"],
    [/临时管制|temporary control/i, "临时管制"],
    [/腐蚀|corrosive|class 8|第8类/i, "腐蚀性危险品运输"],
    [/易燃|flammable|class 3|第3类/i, "易燃危险品运输"],
    [/有毒|毒性|toxic|class 6\.1|第6\.1类/i, "有毒危险品运输"],
  ];
  return categories.find(([pattern]) => pattern.test(text))?.[1] ?? fallback;
}

function normalizeCountryName(value: string | null | undefined): string {
  const normalized = (value ?? "").trim().toLowerCase();
  if (["中国", "cn", "china", "prc"].includes(normalized)) return "中国";
  if (
    ["美国", "us", "usa", "united states", "united states of america"].includes(
      normalized,
    )
  )
    return "美国";
  return value?.trim() ?? "";
}

function historyPrice(item: ProductCommercialHistoryItem): string {
  return `${item.currency ? `${item.currency} ` : ""}${item.amount} · ${item.quantity}`;
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function currentEmailBody(value: string): string {
  return value
    .replace(/\r\n?/g, "\n")
    .replace(/\n{4,}/g, "\n\n\n")
    .trim();
}

function customerProfileDraftFrom(
  customer: CustomerBusinessProfile,
): CustomerProfileDraft {
  return {
    organizationName: customer.organizationName,
    country: customer.country,
    website: customer.website,
    customerGrade: customer.customerGrade,
    profileNarrative:
      customer.profileNarrative ?? customer.qualitySummary ?? "",
    institutionIntroduction:
      customer.profileSections?.institutionIntroduction ?? "",
    industryResearchCapability:
      customer.profileSections?.industryResearchCapability ?? "",
    handlingRecommendation:
      customer.profileSections?.handlingRecommendation ??
      customer.handlingRecommendation ??
      "",
  };
}

function productFieldHint(
  state:
    | {
        state:
          | "EXTRACTED"
          | "AI_SUGGESTED"
          | "HUMAN_CONFIRMED"
          | "NOT_PROVIDED"
          | "UNCONFIRMED";
        source:
          | "EMAIL_BODY"
          | "SUBJECT"
          | "THREAD"
          | "DEEPSEEK"
          | "HUMAN"
          | "UNKNOWN";
        note?: string | null;
      }
    | undefined,
  placement: HintPlacement = "above",
): FieldHint | undefined {
  if (!state) return undefined;
  if (state.state === "HUMAN_CONFIRMED")
    return { text: "人工补充", tone: "human", placement };
  if (state.state === "AI_SUGGESTED")
    return { text: "DeepSeek 建议，待人工确认", tone: "attention", placement };
  if (state.state === "NOT_PROVIDED")
    return {
      text: state.note ?? "邮件正文与已解析线程未提供",
      tone: "default",
      placement,
    };
  if (state.state === "UNCONFIRMED")
    return {
      text: state.note ?? "原文存在但无法确认",
      tone: "attention",
      placement,
    };
  return {
    text:
      state.source === "SUBJECT"
        ? "从邮件主题识别"
        : state.source === "THREAD"
          ? "从邮件线程识别"
          : "从邮件正文识别",
    tone: "default",
    placement,
  };
}

function confirmedFieldsFromInquiry(
  inquiry: InquiryDetailProfile,
): ConfirmedInquiryFields {
  return {
    productName: inquiry.productName ?? "",
    cas: inquiry.cas ?? "",
    catalogNumber: inquiry.catalogNumber ?? "",
    quantity: inquiry.quantity ?? "",
    specification: inquiry.specification ?? "",
    intendedUse: inquiry.intendedUse ?? "",
    destinationCountry: inquiry.destinationCountry ?? "",
  };
}

function MergedGroupRequirement({
  group,
  customerCountry,
  onOpenRisk,
}: {
  group: NonNullable<TriageBusinessWorkbenchDetail["mergedGroup"]>;
  customerCountry: string | null;
  onOpenRisk: (snapshotId: string) => void;
}) {
  return (
    <div className="real-group-requirement">
      <p>
        {group.inquiryCount} 条询单 · {group.productCount} 个产品 · 收货国家{" "}
        {customerCountry ?? "待确认"}
      </p>
      <div className="real-group-requirement-list">
        {group.items.map((member) => (
          <article
            aria-label="产品询单信息"
            className="real-product-evidence-card is-merged-product"
            key={member.snapshotId}
          >
            <ProductInquiryCardContent
              productName={member.product}
              cas={member.cas}
              risk={member.productRiskProfile}
              statusLabel={
                member.processingStatus === "PROCESSED" ? "已处理" : "待处理"
              }
              onOpenRisk={() => onOpenRisk(member.snapshotId)}
              secondaryFields={[
                { label: "Cat No", value: member.catalogNumber },
                { label: "数量", value: member.quantity },
                { label: "规格", value: member.specification },
                { label: "用途", value: null },
                { label: "收货国家", value: member.destinationCountry },
              ]}
            />
            {member.customerMessage || member.projectDetails ? (
              <p className="real-group-inquiry-note">
                客户留言：{member.customerMessage ?? member.projectDetails}
              </p>
            ) : null}
            <ProductCommercialHistory
              history={member.productCommercialHistory}
              productName={member.product}
              cas={member.cas}
            />
          </article>
        ))}
      </div>
    </div>
  );
}

function MergedInquirySummary({
  group,
  onOpenRisk,
}: {
  group: NonNullable<TriageBusinessWorkbenchDetail["mergedGroup"]>;
  onOpenRisk: (snapshotId: string) => void;
}) {
  return (
    <div className="real-merged-group-summary">
      <header>
        <strong>联合询单组</strong>
        <span>
          {group.inquiryCount} 条询单 · {group.productCount} 个产品
        </span>
      </header>
      {group.groupRiskSummary ? (
        <div className="real-group-risk-summary">
          <strong>{group.groupRiskSummary.groupBusinessConclusion}</strong>
          {group.groupRiskSummary.mixedDestinations ? (
            <span>本组产品存在不同收货 / 目的地条件。</span>
          ) : group.groupRiskSummary.sharedRoute ? (
            <span>{group.groupRiskSummary.sharedRoute.routeLabel}</span>
          ) : null}
        </div>
      ) : null}
      <div>
        {group.items.map((member) => (
          <article key={member.snapshotId}>
            <strong>{member.product ?? "产品待确认"}</strong>
            <span>
              {member.cas ? `CAS ${member.cas}` : "CAS 待确认"} ·{" "}
              {member.quantity ?? "数量待确认"}
            </span>
            <small>
              {member.processingStatus === "PROCESSED" ? "已分发" : "待分发"}
            </small>
            {member.riskState ? (
              <button
                type="button"
                className={`real-product-risk-row is-${member.riskState.toLowerCase()}`}
                onClick={() => onOpenRisk(member.snapshotId)}
              >
                <span>{member.riskLabel}</span>
                <small>{businessDisplayNote(member.riskReason)}</small>
                <em>查看依据</em>
              </button>
            ) : null}
            {member.productCommercialHistory.quotes.latest ? (
              <small>
                最近报价：
                {member.productCommercialHistory.quotes.latest.currency}{" "}
                {member.productCommercialHistory.quotes.latest.amount}
              </small>
            ) : null}
            {member.productCommercialHistory.salesPrices?.latest ? (
              <small>
                历史销售价：
                {
                  member.productCommercialHistory.salesPrices.latest.currency
                }{" "}
                {member.productCommercialHistory.salesPrices.latest.amount}
              </small>
            ) : null}
          </article>
        ))}
      </div>
    </div>
  );
}

function GroupRiskPanel({
  group,
  onOpenRisk,
}: {
  group: NonNullable<TriageBusinessWorkbenchDetail["mergedGroup"]>;
  onOpenRisk: (snapshotId: string) => void;
}) {
  const summary = group.groupRiskSummary;
  return (
    <div className="real-group-risk-panel">
      <div className="real-risk-lead">
        <span>整体风险摘要</span>
        <strong>{summary.groupBusinessConclusion}</strong>
      </div>
      {summary.mixedDestinations ? (
        <p className="real-inline-fact">本组产品存在不同收货 / 目的地条件。</p>
      ) : summary.sharedRoute ? (
        <div className="real-route-context">
          {summary.sharedRoute.routeLabel}
        </div>
      ) : null}
      <div className="real-group-risk-products">
        {group.items.map((member) => (
          <button
            type="button"
            key={member.snapshotId}
            className={`is-${member.riskState.toLowerCase()}`}
            onClick={() => onOpenRisk(member.snapshotId)}
          >
            <span>
              <strong>{member.product ?? "产品待确认"}</strong>
              <em>{member.riskLabel}</em>
            </span>
            <small>{businessDisplayNote(member.riskReason)}</small>
            <b>查看依据</b>
          </button>
        ))}
      </div>
      {summary.groupSourcingActions.length ? (
        <div className="real-risk-attention">
          <strong>查货重点</strong>
          <ul>
            {summary.groupSourcingActions.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ProductCommercialHistory({
  history,
  productName,
  cas,
}: {
  history: ProductCommercialHistorySummary;
  productName: string | null;
  cas: string | null;
}) {
  const [detailKind, setDetailKind] = useState<"quote" | null>(() =>
    historyDetailFromUrl(),
  );
  const quote = history.quotes.latest;
  const sale = history.salesPrices?.latest ?? null;
  const detail = detailKind === "quote" && quote ? quote : null;
  useEffect(() => {
    const restore = () => setDetailKind(historyDetailFromUrl());
    window.addEventListener("popstate", restore);
    return () => window.removeEventListener("popstate", restore);
  }, []);
  const openDetail = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("historyDetail", "quote");
    window.history.pushState(null, "", url);
    setDetailKind("quote");
  };
  const closeDetail = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("historyDetail");
    window.history.replaceState(null, "", url);
    setDetailKind(null);
  };
  useEffect(() => {
    if (!detailKind) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeDetail();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [detailKind]);
  if (!quote && !sale) return null;
  return (
    <div className="real-commercial-history">
      <header>
        <strong>历史价格</strong>
        <span>
          {productName ?? "产品待确认"}
          {cas ? ` · CAS ${cas}` : ""}
        </span>
      </header>
      <dl className="real-description-rows">
        {quote ? (
          <div>
            <dt>历史报价</dt>
            <dd>
              <button
                type="button"
                className="real-history-link"
                onClick={openDetail}
              >
                {quote.currency} {quote.amount} · {quote.quantity} ·{" "}
                {formatDate(quote.occurredAt)}
              </button>
            </dd>
          </div>
        ) : null}
        {sale ? (
          <div>
            <dt>历史销售价</dt>
            <dd>
              {historyPrice(sale)} · {formatDate(sale.occurredAt)}
            </dd>
          </div>
        ) : null}
      </dl>
      {detail ? (
        <div className="real-history-detail" role="dialog" aria-modal="true">
          <header>
            <strong>历史报价详情</strong>
            <button type="button" onClick={closeDetail}>
              ×
            </button>
          </header>
          <DescriptionRows
            rows={[
              ["金额", detail.currency + " " + detail.amount],
              ["产品", productName ?? "未记录"],
              ["CAS", cas ?? "未记录"],
              ["数量 / 规格", detail.quantity],
              ["日期", formatDate(detail.occurredAt)],
              ["结果", detail.outcome ?? "未填写"],
            ]}
          />
        </div>
      ) : null}
    </div>
  );
}

function historyDetailFromUrl(): "quote" | null {
  const value = new URL(window.location.href).searchParams.get("historyDetail");
  return value === "quote" ? value : null;
}

function DescriptionRows({ rows }: { rows: Array<[string, string]> }) {
  return (
    <dl className="real-description-rows">
      {rows.map(([label, value], index) => (
        <div key={`${label}:${index}`}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

type CustomerEvidenceSource =
  | "EMAIL_BODY"
  | "EMAIL_DOMAIN"
  | "CRM_HISTORY"
  | "DEEPSEEK"
  | "HUMAN"
  | "RULE"
  | "OFFICIAL_SOURCE";

function CustomerProfileField({
  label,
  value,
  href,
  sources,
  note,
  noteTone,
  placement = "below",
  wide = false,
}: {
  label: string;
  value: string;
  href?: string;
  sources?: CustomerEvidenceSource[];
  note?: string | null;
  noteTone?: "attention";
  placement?: HintPlacement;
  wide?: boolean;
}) {
  return (
    <div className={wide ? "is-wide" : undefined}>
      <dt>{label}</dt>
      <dd>
        <span className="real-profile-field-value">
          {href ? (
            <a
              href={customerWebsiteHref(href)}
              target="_blank"
              rel="noreferrer"
            >
              {value}
            </a>
          ) : (
            value
          )}
          {sources?.length ? (
            <InlineHint
              label={`${label}字段来源`}
              placement={placement}
              text={sources.map(customerEvidenceSourceLabel).join(" · ")}
            />
          ) : null}
        </span>
        {note ? (
          <small
            className={
              noteTone === "attention"
                ? "real-profile-field-note is-attention"
                : "real-profile-field-note"
            }
          >
            {note}
          </small>
        ) : null}
      </dd>
    </div>
  );
}

function organizationIdentityStatusLabel(
  status: CustomerBusinessProfile["organizationIdentityStatus"],
): string | null {
  if (status === "AI_SUGGESTED") return "DeepSeek 机构建议，待人工确认";
  if (status === "CRM_CANDIDATE") return "CRM 机构记录，待人工确认";
  if (status === "DOMAIN_CANDIDATE") return "邮箱域名识别候选，待人工确认";
  if (status === "UNKNOWN") return "机构身份待人工补充";
  return null;
}

function customerWebsiteHref(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function customerEvidenceSourceLabel(source: CustomerEvidenceSource): string {
  if (source === "DEEPSEEK") return "DeepSeek 证据支持";
  if (source === "EMAIL_DOMAIN") return "邮箱域名";
  if (source === "EMAIL_BODY") return "邮件原文";
  if (source === "CRM_HISTORY") return "CRM 历史";
  if (source === "HUMAN") return "人工修正";
  if (source === "OFFICIAL_SOURCE") return "机构资料";
  return "业务规则";
}

function customerIntelligenceLabel(customer: CustomerBusinessProfile): string {
  const intelligence = customer.intelligence;
  if (!intelligence) return "客户画像来源待核对";
  if (intelligence.status === "READY") {
    return intelligence.sourceMode === "HYBRID"
      ? "DeepSeek 建议已与域名、原文及 CRM 事实交叉校验"
      : "DeepSeek 客户画像建议已形成";
  }
  if (intelligence.status === "READY_WITHOUT_RESULT")
    return "DeepSeek 已运行，但未形成可用客户画像";
  if (intelligence.status === "RUNNING") return "DeepSeek 正在分析客户画像";
  if (intelligence.status === "FAILED") return "DeepSeek 客户画像分析失败";
  if (intelligence.status === "STALE") return "DeepSeek 客户画像已过期";
  return "DeepSeek 尚未分析客户画像";
}

function CustomerSources({ customer }: { customer: CustomerBusinessProfile }) {
  const websiteCandidate = customer.website;
  return (
    <div className="real-peek-stack">
      <section className="real-source-section real-intelligence-source-section">
        <h3>客户画像智能分析</h3>
        <strong>{customerIntelligenceLabel(customer)}</strong>
        <small>
          这里只展示被当前画像采用且可以回看来源的分析依据；客户等级和历史关系仍以人工与
          CRM 事实为准。
        </small>
      </section>
      <section className="real-source-section">
        <h3>客户画像判断证据</h3>
        {customer.profileEvidence?.length ? (
          <div className="real-profile-evidence-list">
            {customer.profileEvidence.map((evidence) => (
              <article
                className="real-profile-evidence-item"
                key={evidence.reference + evidence.statement}
              >
                <div>
                  <strong>{profileEvidenceRoleLabel(evidence.role)}</strong>
                  <span>
                    {profileEvidenceReferenceLabel(evidence.reference)}
                  </span>
                </div>
                <p>{evidence.statement}</p>
              </article>
            ))}
          </div>
        ) : (
          <p className="real-peek-hint">
            当前结果没有可核验的客户画像证据，因此未将模型描述作为正式画像字段采用。
          </p>
        )}
      </section>
      <section className="real-source-section">
        <h3>网站候选</h3>
        {websiteCandidate ? (
          <a
            href={customerWebsiteHref(websiteCandidate)}
            target="_blank"
            rel="noreferrer"
          >
            {websiteCandidate}
          </a>
        ) : (
          <p className="real-peek-hint">尚无经过当前证据链支持的网站候选。</p>
        )}
      </section>
      <section className="real-source-section">
        <h3>判断资料来源</h3>
        {customer.sources.length ? (
          customer.sources.map((source) => (
            <article
              className="real-source-item"
              key={(source.id ?? source.label) + "-" + source.checkedAt}
            >
              <strong>{source.label}</strong>
              {source.domain ? <span>{source.domain}</span> : null}
              <span>
                来源类型：
                {source.sourceType === "INTERNAL_CRM"
                  ? "CRM 业务记录"
                  : source.sourceType === "OFFICIAL"
                    ? "官方资料"
                    : source.sourceType === "PUBLIC_WEB"
                      ? "公开网页"
                      : "业务资料"}
              </span>
              {source.usedFor ? <span>用于判断：{source.usedFor}</span> : null}
              {source.url ? (
                <a href={source.url} target="_blank" rel="noreferrer">
                  打开来源
                </a>
              ) : null}
            </article>
          ))
        ) : (
          <p className="real-peek-hint">暂无可核对的客户资料来源。</p>
        )}
      </section>
    </div>
  );
}

function profileEvidenceRoleLabel(
  role: "FACT" | "INFERENCE" | "MISSING" | "CONTRADICTION",
): string {
  if (role === "FACT") return "事实依据";
  if (role === "INFERENCE") return "分析依据";
  if (role === "CONTRADICTION") return "冲突证据";
  return "缺失信息";
}

function profileEvidenceReferenceLabel(reference: string): string {
  if (reference.startsWith("SITE_")) return `机构官方资料 · ${reference}`;
  if (reference.startsWith("CRM_HISTORY_")) return `CRM 历史 · ${reference}`;
  if (reference.startsWith("CRM_ORDER_")) return `成交记录 · ${reference}`;
  if (reference.startsWith("MSG_")) return `DeepSeek 引用邮件 · ${reference}`;
  if (reference.startsWith("DOMAIN_"))
    return `DeepSeek 引用域名 · ${reference}`;
  if (reference.startsWith("THREAD_")) return "邮件原文确定性证据";
  if (reference === "VERIFIED_DOMAIN_PROFILE") return "已核验机构域名资料";
  return reference;
}

function HistoryPeek({
  history: inquiryHistory,
  distribution,
}: {
  history: InquiryHistorySummary;
  distribution: DistributionDecision;
}) {
  const [windowKey, setWindowKey] = useState<"90d" | "1y" | "all">("90d");
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(
    null,
  );
  const history = distribution.recentReusableHistory;
  const cutoff =
    windowKey === "90d"
      ? Date.now() - 90 * 24 * 60 * 60 * 1000
      : windowKey === "1y"
        ? Date.now() - 365 * 24 * 60 * 60 * 1000
        : 0;
  const visibleItems = inquiryHistory.items.filter(
    (item) => Date.parse(item.submittedAt) >= cutoff,
  );
  const selectedHistory = inquiryHistory.items.find(
    (item) => item.snapshotId === selectedHistoryId,
  );
  return (
    <div className="real-peek-stack">
      <article className="real-history-summary">
        <strong>历史询单</strong>
        <DescriptionRows
          rows={[
            ["全部历史", `${inquiryHistory.total} 条`],
            ["近90天", `${inquiryHistory.last90d} 条`],
            ["近1年", `${inquiryHistory.last1y} 条`],
            ["当前待处理", `${inquiryHistory.open} 条`],
          ]}
        />
        <div
          className="real-history-filter"
          role="group"
          aria-label="历史时间范围"
        >
          {(["90d", "1y", "all"] as const).map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={windowKey === key}
              onClick={() => setWindowKey(key)}
            >
              {key === "90d" ? "近90天" : key === "1y" ? "近1年" : "全部"}
            </button>
          ))}
        </div>
        <small>当前范围：{visibleItems.length} 条</small>
      </article>
      {visibleItems.map((item) => (
        <article key={item.snapshotId} className="real-history-entry">
          <header>
            <strong>{item.anonymousReference}</strong>
            <span>{formatDate(item.submittedAt)}</span>
          </header>
          <span>
            {item.workspace} · {item.site}
          </span>
          <span>
            {item.product ?? "产品未记录"} · {item.cas ?? "CAS 未记录"}
          </span>
          <span>
            {item.quantity ?? "数量未记录"} ·{" "}
            {item.status === "PENDING" ? "待处理" : "已处理"}
          </span>
          <button
            type="button"
            onClick={() => setSelectedHistoryId(item.snapshotId)}
          >
            查看询单详情
          </button>
        </article>
      ))}
      {selectedHistory ? (
        <article className="real-history-detail">
          <strong>历史询单详情</strong>
          <DescriptionRows
            rows={[
              ["询单编号", selectedHistory.anonymousReference],
              ["提交时间", formatDate(selectedHistory.submittedAt)],
              ["产品", selectedHistory.product ?? "未记录"],
              ["CAS", selectedHistory.cas ?? "未记录"],
              ["数量", selectedHistory.quantity ?? "未记录"],
              ["案件", selectedHistory.caseId ?? "尚未关联"],
              ["报价", selectedHistory.quoted ? "已报价" : "未发现报价"],
              ["订单", selectedHistory.ordered ? "已成交" : "未发现订单"],
            ]}
          />
          <button type="button" onClick={() => setSelectedHistoryId(null)}>
            返回历史列表
          </button>
        </article>
      ) : null}
      <p>{recentHistoryLabel(distribution)}</p>
      <DescriptionRows
        rows={[
          [
            "匹配方式",
            history.matchedBy === "CAS"
              ? "精确 CAS"
              : history.matchedBy === "CATALOG_NUMBER"
                ? "Cat No"
                : history.matchedBy === "PRODUCT_NAME"
                  ? "唯一标准化产品名称"
                  : "无",
          ],
          ["匹配记录", `${history.matchedRecords} 条`],
          ["最近查货", history.latestAt ? formatDate(history.latestAt) : "无"],
          ["历史数量", history.historicalQuantity ?? "无"],
          ["历史销售负责人", history.historicalSalesOwner ?? "无"],
        ]}
      />
      <small>关联历史仅嵌入当前询单上下文，不会切换或创建其他询单。</small>
    </div>
  );
}

function RiskPeek({
  productName,
  cas,
  risk,
}: {
  productName: string | null | undefined;
  cas: string | null | undefined;
  risk: ProductRiskProfile;
}) {
  const operatorView = productRiskOperatorView(risk);
  const deepSeekEvidence = risk.deepSeekAssessment?.evidence ?? [];
  return (
    <div className="real-peek-stack">
      <article className="product-risk-evidence-overview">
        <header>
          <strong>产品风险判断</strong>
          <p>{operatorView.handlingConclusion}</p>
        </header>
        <div className="product-risk-intelligence-status">
          <strong>{productRiskIntelligenceLabel(risk)}</strong>
          <span>{productRiskIntelligenceHelp(risk)}</span>
        </div>
        <dl className="product-risk-evidence-identity" aria-label="产品身份">
          <div>
            <dt>产品</dt>
            <dd>{productName?.trim() || "无"}</dd>
          </div>
          <div>
            <dt>CAS</dt>
            <dd>{cas?.trim() || "无"}</dd>
          </div>
        </dl>
        <section
          className="product-risk-evidence-list"
          aria-label="分项判断依据"
        >
          {operatorView.dimensions.map((dimension) => (
            <article
              className={`product-risk-evidence-detail ${operatorRiskStateClass(dimension.state)}`}
              key={dimension.key}
            >
              <header>
                <span>{dimension.label}</span>
                <strong>{dimension.conclusion}</strong>
              </header>
              {dimension.state !== "NO_RISK_FOUND" &&
              dimension.evidenceSummary ? (
                <p>{dimension.evidenceSummary}</p>
              ) : null}
              {dimension.state !== "NO_RISK_FOUND" && dimension.sourceLabel ? (
                dimension.sourceUrl ? (
                  <a
                    href={dimension.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    查看{dimension.sourceLabel}
                  </a>
                ) : (
                  <small>依据来源：{dimension.sourceLabel}</small>
                )
              ) : null}
            </article>
          ))}
        </section>
      </article>
      <ResearchEvidencePeek evidence={risk.researchEvidence} />
      {deepSeekEvidence.length ? (
        <article className="real-evidence-card real-research-evidence">
          <header>
            <strong>辅助分析依据</strong>
            <em>DeepSeek 建议，需结合原始资料人工核验</em>
          </header>
          <ul>
            {deepSeekEvidence.map((entry) => (
              <li key={`${entry.reference}:${entry.statement}`}>
                <span>{productRiskEvidenceStatement(entry.statement)}</span>
                {isPublicReference(entry.reference) ? (
                  <a href={entry.reference} target="_blank" rel="noreferrer">
                    查看原始资料
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        </article>
      ) : null}
    </div>
  );
}

function productRiskIntelligenceLabel(risk: ProductRiskProfile): string {
  const intelligence = risk.intelligence;
  if (intelligence?.status === "READY" && intelligence.resultAvailable) {
    if (!intelligence.broadWebSearchExecuted)
      return "DeepSeek 已分析当前已提供资料；尚未执行开放网络检索";
    if (!intelligence.sdsSourceAvailable)
      return "DeepSeek 已完成公开资料检索；尚未取得可信 SDS";
    return intelligence.coverage === "COMPLETE"
      ? "DeepSeek 已完成当前产品的风险分析"
      : "DeepSeek 已完成分析；当前公开资料未形成完整风险结论";
  }
  if (intelligence?.status === "RUNNING") return "DeepSeek 正在分析当前产品";
  if (intelligence?.status === "READY_WITHOUT_RESULT")
    return "DeepSeek 已执行，但未形成可用产品风险结果";
  if (intelligence?.status === "FAILED") return "DeepSeek 产品风险分析失败";
  if (intelligence?.status === "STALE") return "DeepSeek 产品风险结果已过期";
  return "当前产品尚未执行可核验的 DeepSeek 风险分析";
}

function productRiskIntelligenceHelp(risk: ProductRiskProfile): string {
  const intelligence = risk.intelligence;
  if (intelligence?.status === "READY" && intelligence.resultAvailable) {
    if (!intelligence.broadWebSearchExecuted)
      return "当前只分析了系统已提供的产品与监管资料，不能视为完成产品级公开网络检索；查货人员仍需核对法规、SDS 与承运条件。";
    if (!intelligence.sdsSourceAvailable)
      return "已完成公开资料检索，但没有取得制造商或可信产品 SDS；运输与物态结论仍需查货人员核实。";
    return intelligence.coverage === "COMPLETE"
      ? "以下结论来自当前产品的有效分析结果；有风险时同时展示具体依据。"
      : "已按产品名称与 CAS 核对现有公开资料；分项中仅保留可由来源支持的结论。";
  }
  return "现有页面不作安全推断，查货人员需要按中国、美国、目标国、运输和气体五个维度逐项核查。";
}

function ResearchEvidencePeek({
  evidence,
}: {
  evidence?: ProductRiskProfile["researchEvidence"];
}) {
  if (!evidence || (!evidence.findings.length && !evidence.sources.length))
    return null;
  const conflicts = evidence.findings.filter((finding) => finding.contradicts);
  return (
    <article className="real-evidence-card real-research-evidence">
      <header>
        <strong>可核验资料</strong>
      </header>
      {evidence.findings.length ? (
        <ul>
          {evidence.findings.map((finding) => (
            <li key={finding.id}>
              <span>{finding.fact}</span>
              <a href={finding.sourceUrl} target="_blank" rel="noreferrer">
                {finding.sourceTitle}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      {conflicts.length ? (
        <div className="real-risk-attention">
          <strong>冲突证据</strong>
          <ul>
            {conflicts.slice(0, 3).map((finding) => (
              <li key={finding.id}>{finding.fact}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {evidence.sources.length ? (
        <details>
          <summary>查看补充参考资料</summary>
          <ul>
            {evidence.sources.map((source) => (
              <li key={source.id}>
                <a href={source.url} target="_blank" rel="noreferrer">
                  {source.title}
                </a>
                <small>
                  {source.domain} · {source.sourceType}
                </small>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </article>
  );
}

function isPublicReference(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

export function productRiskEvidenceStatement(value: string): string {
  const normalized = value.trim();
  if (/Official regulatory framework|仅框架参考/i.test(normalized)) {
    const authority = normalized.split(/[：:]/, 1)[0]?.trim() || "官方监管";
    return `已查阅${authority}监管规则页面；该页面用于界定规则范围，不能单独证明本产品未受管制。`;
  }
  return businessDisplayNote(normalized) || "该资料仅供产品风险人工核验。";
}

function triageDecisionWorkbenchLabel(profile: TriageDecisionProfile): string {
  const state = triageBusinessState(profile);
  if (state === "AI_UNAVAILABLE") return "AI 分析暂未完成（人工规则建议）";
  if (state === "CONFIRMED_HARD_BLOCK") return "存在明确阻断事实";
  if (state === "AI_RISK_UNCERTAINTY") return "可以继续，查货时确认风险";
  if (profile.overallStatus === "PASS") return "可以正常处理";
  if (profile.overallStatus === "WARNING") return "可以继续处理，但需注意风险";
  if (profile.overallStatus === "PENDING") return "需要人工复核";
  if (profile.overallStatus === "REJECT") return "不建议承接";
  if (profile.analysisStatus === "STALE") return "正在重新分析…";
  return "AI 正在分析…";
}

function triageDecisionProfileOf(value: {
  triageDecisionProfile?: TriageDecisionProfile;
}): TriageDecisionProfile {
  return (
    value.triageDecisionProfile ?? {
      overallStatus: "ANALYZING",
      businessState: "ANALYZING",
      analysisStatus: "NOT_STARTED",
      source: "UNKNOWN",
      evidenceStatus: "UNVERIFIED",
      reason: "后台分诊结果尚未就绪。",
      requiresReview: false,
      agentDefinitionVersion: "TRIAGE_AGENT_V1:1",
      agentRunId: null,
      inputHash: null,
      artifactVersion: null,
      createdAt: null,
    }
  );
}

function assessmentLabel(
  dimension: ProductRiskAssessment["dimension"],
): string {
  const labels: Record<typeof dimension, string> = {
    PHYSICAL_STATE: "物理状态",
    TOXICITY: "毒性",
    STABILITY: "稳定性",
    DANGEROUS_GOODS: "危险品",
    TRANSPORT: "运输",
    CHINA_REGULATION: "中国监管",
    US_REGULATION: "美国出口管制 / 制裁",
    DESTINATION_REGULATION: "目标国监管",
    END_USER: "最终用户",
  };
  return labels[dimension];
}

function businessDecisionSummary(
  decision: TriageDecisionProfile,
  distribution: DistributionDecision,
  risk: ProductRiskProfile,
): string {
  const state = triageBusinessState(decision);
  if (state === "AI_UNAVAILABLE")
    return "AI 分析暂未完成；当前建议来自人工规则，可按人工判断继续分发";
  if (state === "CONFIRMED_HARD_BLOCK")
    return `暂停普通分发；${businessDisplayNote(decision.reason) || "已确认存在业务阻断"}`;
  if (state === "BUSINESS_REVIEW_REQUIRED")
    return `先处理业务异常；${businessDisplayNote(decision.reason) || "需要人工复核"}`;
  if (state === "ANALYZING") return "正在形成业务判断";
  if (state === "AI_RISK_UNCERTAINTY")
    return `可以继续分发；${sourcingSummary(risk)}`;
  if (distribution.action === "CONTINUE_EXISTING_CASE")
    return distribution.linkedCase
      ? `继续已匹配案件：${distribution.linkedCase.reference}`
      : "继续已匹配案件";
  return distribution.action === "SALES_DIRECT_QUOTE"
    ? "可以继续，已有近期有效结果可供报价"
    : "可以继续分发";
}

function triageBusinessState(
  decision: TriageDecisionProfile,
): NonNullable<TriageDecisionProfile["businessState"]> {
  if (decision.businessState) return decision.businessState;
  if (decision.analysisStatus === "FAILED") return "AI_UNAVAILABLE";
  if (decision.overallStatus === "ANALYZING") return "ANALYZING";
  if (decision.source === "HARD_FACT" && decision.overallStatus === "REJECT")
    return "CONFIRMED_HARD_BLOCK";
  if (decision.requiresReview) return "BUSINESS_REVIEW_REQUIRED";
  if (["WARNING", "PENDING", "REJECT"].includes(decision.overallStatus))
    return "AI_RISK_UNCERTAINTY";
  return "CLEAR";
}

function sourcingSummary(risk: ProductRiskProfile): string {
  const attention = (risk.assessments ?? []).filter((assessment) =>
    isAttentionJudgment(assessment.judgment),
  );
  const hasFlammability = attention.some((assessment) =>
    /易燃/.test(assessment.reason),
  );
  const hasToxicity = attention.some(
    (assessment) => assessment.dimension === "TOXICITY",
  );
  const hasTransport = attention.some((assessment) =>
    ["DANGEROUS_GOODS", "TRANSPORT"].includes(assessment.dimension),
  );
  if (hasFlammability && hasTransport)
    return "查货时确认易燃性和危险品运输条件";
  if (hasToxicity && hasTransport) return "查货时重点确认毒性和危险品运输条件";
  if (hasTransport) return "查货时确认危险品与运输条件";
  if (hasToxicity) return "查货时确认毒性与供应商 SDS 分类";
  return "查货时确认产品合规与供货条件";
}

function businessRiskRows(
  risk: ProductRiskProfile,
  inquiry: InquiryDetailProfile,
): Array<[string, string]> {
  const assessments = relevantAssessments(risk);
  const rows = assessments
    .sort(
      (left, right) =>
        judgmentPriority(left.judgment) - judgmentPriority(right.judgment),
    )
    .map((assessment): [string, string] => [
      assessment.dimension === "DESTINATION_REGULATION"
        ? destinationBusinessLabel(risk.destinationLabel)
        : assessment.dimension === "US_REGULATION"
          ? usBusinessLabel(risk)
          : assessmentLabel(assessment.dimension),
      assessmentBusinessConclusion(assessment, risk),
    ]);
  const presentDimensions = new Set(
    assessments.map((assessment) => assessment.dimension),
  );
  if (!presentDimensions.has("CHINA_REGULATION"))
    rows.push(["中国监管", controlLabel[risk.chinaControl]]);
  if (!presentDimensions.has("US_REGULATION"))
    rows.push([usBusinessLabel(risk), controlLabel[risk.usControl]]);
  if (
    !presentDimensions.has("DESTINATION_REGULATION") &&
    routeContextOf(risk).destinationCountry !==
      routeContextOf(risk).usTransitOrHub
  )
    rows.push([
      destinationBusinessLabel(risk.destinationLabel),
      controlLabel[risk.destinationControl],
    ]);
  if (!risk.assessments?.length && risk.findings.length)
    rows.unshift(
      ...risk.findings.map((finding): [string, string] => [
        finding.title,
        finding.severity === "BLOCKING"
          ? `已确认：${businessDisplayNote(finding.reason)}`
          : `需确认：${businessDisplayNote(finding.reason)}`,
      ]),
    );
  if (inquiry.quantity) {
    rows.push([
      "数量",
      risk.quantityRisk === "NORMAL"
        ? `${inquiry.quantity}，未见数量异常`
        : risk.quantityRisk === "ABNORMAL"
          ? `${inquiry.quantity}，数量异常，需人工核对`
          : `${inquiry.quantity}，需查货时确认包装与数量`,
    ]);
  }
  return rows;
}

function riskMainRows(risk: ProductRiskProfile): Array<[string, string]> {
  const route = routeContextOf(risk);
  return [
    [
      "风险证据状态",
      RISK_STATE_LABEL[risk.riskState ?? "INSUFFICIENT_EVIDENCE"].label,
    ],
    ["中国管制", controlLabel[risk.chinaControl]],
    [
      usBusinessLabel(risk),
      risk.usControl === "RESTRICTED"
        ? "存在进口 / 转运 / 再出口限制"
        : controlLabel[risk.usControl],
    ],
    [
      route.destinationCountry === route.usTransitOrHub
        ? "美国进口 / 目的地监管"
        : destinationBusinessLabel(risk.destinationLabel),
      risk.destinationControl === "RESTRICTED"
        ? "存在目的地监管限制"
        : controlLabel[risk.destinationControl],
    ],
    ["运输风险", riskTransportSummary(risk)],
    ["是否气体", gasBusinessLabel(risk)],
  ];
}

function gasBusinessLabel(risk: ProductRiskProfile): string {
  if (risk.gasBusinessState === "CONFIRMED_GAS")
    return "已确认气体（不可承接）";
  if (risk.gasBusinessState === "GAS_CONFIRMATION_REQUIRED")
    return "需确认气态属性";
  if (risk.gasBusinessState === "NOT_GAS") return "否";
  if (risk.isGas === "YES") return "是";
  if (risk.isGas === "PENDING") return "待确认";
  return "否";
}

function riskTransportSummary(risk: ProductRiskProfile): string {
  if (risk.transportRisk === "NORMAL") return "常规运输";
  if (risk.transportRisk === "UNAVAILABLE") return "当前不可运输";
  if (risk.transportRisk === "PENDING") return "需确认承运条件";
  return risk.transportSummary || "需要特殊运输条件";
}

function relevantAssessments(
  risk: ProductRiskProfile,
): ProductRiskAssessment[] {
  return (risk.assessments ?? []).filter((assessment) => {
    if (
      assessment.dimension === "DESTINATION_REGULATION" &&
      routeContextOf(risk).destinationCountry ===
        routeContextOf(risk).usTransitOrHub
    )
      return false;
    if (["PHYSICAL_STATE", "END_USER"].includes(assessment.dimension))
      return isAttentionJudgment(assessment.judgment);
    return true;
  });
}

function assessmentBusinessConclusion(
  assessment: ProductRiskAssessment,
  risk?: ProductRiskProfile,
): string {
  if (["LOW_RISK", "LIKELY_NORMAL", "UNLIKELY"].includes(assessment.judgment)) {
    if (assessment.dimension === "CHINA_REGULATION")
      return "当前未发现管控要求";
    if (assessment.dimension === "US_REGULATION")
      return risk &&
        routeContextOf(risk).destinationCountry !==
          routeContextOf(risk).usTransitOrHub
        ? "当前未发现明显转运 / 再出口限制"
        : "当前未发现明显进口 / 目的地限制";
    if (assessment.dimension === "DESTINATION_REGULATION")
      return "当前未发现特殊管控要求";
    return businessDisplayNote(assessment.reason);
  }
  if (assessment.judgment === "PENDING")
    return `待确认：${businessDisplayNote(assessment.reason)}`;
  if (assessment.judgment === "WARNING")
    return `需确认：${businessDisplayNote(assessment.reason)}`;
  return `重点关注：${businessDisplayNote(assessment.reason)}`;
}

function assessmentNextAction(assessment: ProductRiskAssessment): string {
  if (!isAttentionJudgment(assessment.judgment)) return "";
  if (assessment.dimension === "TOXICITY")
    return "查货时核对供应商 SDS 中的毒性分类";
  if (assessment.dimension === "DANGEROUS_GOODS")
    return "查货时核对供应商 SDS 与危险品分类";
  if (assessment.dimension === "TRANSPORT") return "查货时确认承运条件";
  if (assessment.dimension === "STABILITY") return "查货时确认储存和稳定性要求";
  if (assessment.dimension === "PHYSICAL_STATE")
    return "查货时确认包装与交付形态";
  return "查货时确认适用监管要求";
}

function sourcingAttentionItems(risk: ProductRiskProfile): string[] {
  const assessmentActions = relevantAssessments(risk)
    .filter((assessment) => isAttentionJudgment(assessment.judgment))
    .map(assessmentNextAction);
  const findingActions = !risk.assessments?.length
    ? risk.findings.map((finding) => finding.recommendation)
    : [];
  return Array.from(
    new Set([...assessmentActions, ...findingActions].filter(Boolean)),
  );
}

function isAttentionJudgment(value: string): boolean {
  return ["WARNING", "PENDING", "HIGH_RISK", "RESTRICTED"].includes(value);
}

function judgmentPriority(value: string): number {
  if (["HIGH_RISK", "RESTRICTED"].includes(value)) return 0;
  if (value === "WARNING") return 1;
  if (value === "PENDING") return 2;
  return 3;
}

function destinationBusinessLabel(value: string): string {
  return destinationLabel(value).replace(/管制$/, "监管");
}

function usBusinessLabel(risk: ProductRiskProfile): string {
  return routeContextOf(risk).destinationCountry ===
    routeContextOf(risk).usTransitOrHub
    ? "美国进口 / 目的地监管"
    : "美国转运 / 再出口监管";
}

function routeContextOf(
  risk: ProductRiskProfile,
  inquiry?: InquiryDetailProfile,
): NonNullable<ProductRiskProfile["routeContext"]> {
  if (risk.routeContext) return risk.routeContext;
  const destination = inquiry?.destinationCountry ?? null;
  return {
    originCountry: "中国",
    usTransitOrHub: "美国",
    destinationCountry: destination,
    routeLabel:
      destination === "美国"
        ? "中国 → 美国（进口 / 目的地）"
        : `中国 → 美国 → ${destination ?? "目标国家"}`,
  };
}

function riskDimensions(risk: ProductRiskProfile) {
  const finding = (
    dimension: ProductRiskProfile["findings"][number]["dimension"],
  ) => risk.findings.find((entry) => entry.dimension === dimension);
  return [
    {
      key: "CHINA_CONTROL",
      label: "中国管制",
      conclusion: controlLabel[risk.chinaControl],
      finding: finding("CHINA_CONTROL"),
    },
    {
      key: "US_CONTROL",
      label: usBusinessLabel(risk),
      conclusion: controlLabel[risk.usControl],
      finding: finding("US_CONTROL"),
    },
    {
      key: "DESTINATION_CONTROL",
      label: destinationLabel(risk.destinationLabel),
      conclusion: controlLabel[risk.destinationControl],
      finding: finding("DESTINATION_CONTROL"),
    },
    {
      key: "DANGEROUS_GOODS",
      label: "危险品",
      conclusion: yesNoLabel[risk.dangerousGoods],
      finding: finding("DANGEROUS_GOODS"),
    },
    {
      key: "TRANSPORT",
      label: "运输",
      conclusion: transportLabel[risk.transportRisk],
      finding: finding("TRANSPORT"),
    },
    {
      key: "GAS",
      label: "气体 / 物态",
      conclusion: yesNoLabel[risk.isGas],
      finding: finding("GAS"),
    },
    {
      key: "END_USER",
      label: "最终用户",
      conclusion: controlLabel[risk.endUserRisk],
      finding: finding("END_USER"),
    },
  ].filter(
    (dimension) =>
      dimension.key !== "DESTINATION_CONTROL" ||
      routeContextOf(risk).destinationCountry !==
        routeContextOf(risk).usTransitOrHub,
  );
}

function destinationLabel(value: string): string {
  return value === "美国管制" ? "美国目的地 / 进口限制" : value;
}

function DuplicatePeek({
  profile,
}: {
  profile: TriageBusinessWorkbenchDetail["duplicateProfile"];
}) {
  const duplicates = profile.duplicateCandidates ?? [];
  return (
    <div className="real-peek-stack">
      <p>{profile.summary}</p>
      {duplicates.length ? (
        <section className="real-peek-section">
          <h3>重复询单（{duplicates.length} 条）</h3>
          {duplicates.map((candidate) => (
            <DuplicateCandidateCard
              key={candidate.snapshotId}
              candidate={candidate}
            />
          ))}
        </section>
      ) : (
        <p className="real-peek-hint">当前没有需要人工复核的重复询单。</p>
      )}
    </div>
  );
}

function DuplicateCandidateCard({
  candidate,
}: {
  candidate: TriageBusinessWorkbenchDetail["duplicateProfile"]["candidates"][number];
}) {
  const processed = Boolean(candidate.caseId) || candidate.status !== "PENDING";
  return (
    <article>
      <header>
        <strong>{candidate.product ?? "产品待确认"}</strong>
        <em>{relationshipLabel(candidate.relationship)}</em>
      </header>
      <span>提交时间：{formatDate(candidate.submittedAt)}</span>
      {candidate.product ? <span>产品：{candidate.product}</span> : null}
      {candidate.cas ? <span>CAS：{candidate.cas}</span> : null}
      {candidate.quantity ? <span>数量：{candidate.quantity}</span> : null}
      <span>
        状态：
        {processed
          ? candidate.caseId
            ? "已处理（已并入案件）"
            : "已处理"
          : "待处理"}
      </span>
      {candidate.differences
        .filter(isBusinessMergeDifference)
        .map((difference) => (
          <span key={difference}>需注意：{difference}</span>
        ))}
    </article>
  );
}

function isBusinessMergeDifference(value: string): boolean {
  return /联系人|邮箱|目的地|国家|项目|用途|采购|机构|公司/i.test(value);
}

function relationshipLabel(
  relationship: TriageBusinessWorkbenchDetail["duplicateProfile"]["candidates"][number]["relationship"],
): string {
  if (relationship === "DUPLICATE") return "重复询单";
  if (relationship === "MERGE_CANDIDATE") return "合并候选";
  return "相关询单";
}

function candidateLabel(
  candidate: DistributionDecision["salesCandidate"],
): string {
  return candidate ? candidate.alias : "暂无可用成员";
}

function recentHistoryLabel(distribution: DistributionDecision): string {
  const history = distribution.recentReusableHistory;
  if (history.status === "REUSABLE") return "已有近期有效查货结果，可直接报价";
  if (history.status === "FAILED_HISTORY")
    return "近期无货 / 失败记录不可复用，需要重新查货";
  return "当前 CRM 未匹配到可复用报价";
}

function peekTitle(peek: Exclude<Peek, null>): string {
  if (peek === "MAIL") return "邮件判断依据";
  if (peek === "CUSTOMER") return "客户资料来源";
  if (peek === "HISTORY") return "关联历史查货与报价";
  if (peek === "DUPLICATE") return "询单关系与差异";
  return "产品风险判断依据";
}

function display(value: string | null): string {
  return value ?? "未填写";
}

function caseStatusLabel(value: string): string {
  const labels: Record<string, string> = {
    NEW: "新建",
    TRIAGE: "分诊中",
    QUALIFYING: "资格确认",
    SOURCING: "查货中",
    TECHNICAL_REVIEW: "技术复核",
    OPEN: "处理中",
    IN_PROGRESS: "处理中",
    QUOTING: "报价中",
    WAITING_CUSTOMER: "等待客户",
    WAITING_INTERNAL: "等待内部处理",
    WON: "已成交",
    LOST: "未成交",
    CLOSED: "已关闭",
    ARCHIVED: "已归档",
  };
  return labels[value] ?? "状态待确认";
}

function sourceSiteLabel(value: string): string {
  if (/BENCHCHEM/i.test(value)) return "BenchChem";
  if (/PRIMARY_SITE/i.test(value)) return "EvitaChem";
  return value;
}

function peekFromUrl(): Peek {
  const value = new URLSearchParams(window.location.search).get("peek");
  if (value === "mail") return "MAIL";
  if (value === "customer") return "CUSTOMER";
  if (value === "history") return "HISTORY";
  if (value === "risk") return "RISK";
  if (value === "duplicate") return "DUPLICATE";
  return null;
}

function sourcePageLabel(value: string | null): string {
  if (!value) return "未填写";
  if (/product[-_/ ]?detail/i.test(value) || /\/product\//i.test(value))
    return "产品详情页";
  return value;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString("zh-CN", { hour12: false });
}
